import React, { useCallback, useEffect, useMemo, useState } from "react";
import MoodBadRoundedIcon from "@mui/icons-material/MoodBadRounded";
import SentimentSatisfiedAltRoundedIcon from "@mui/icons-material/SentimentSatisfiedAltRounded";
import omnichannelInboxApi from "../../services/omnichannelInboxApi";

const DAY_WORDS =
  "monday|tuesday|wednesday|thursday|friday|saturday|sunday";

const WORD_NUMBERS = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
};

// Visa requirements for Zimbabwe passport holders (client-side subset)
// ── VISA_DB: visa requirements for Zimbabwe passport holders ─────────────────
// Types: visa_required | e_visa | eta_required | visa_on_arrival | visa_free
// Always advise clients to confirm requirements at the destination embassy before booking
// as policies change. This DB is a guide for consultants, not a legal guarantee.
const VISA_DB = {

  // ═══════════════════════════════════════════════════════════════════════
  // VISA REQUIRED (embassy / consulate application before travel)
  // ═══════════════════════════════════════════════════════════════════════

  // United Kingdom
  uk: { type: "visa_required", label: "UK visa required", action: "Ask if client has a valid UK Standard Visitor visa — apply via UKVI (gov.uk/apply-uk-visa). Processing can take 3–8 weeks." },
  "united kingdom": { type: "visa_required", label: "UK visa required", action: "Ask if client has a valid UK Standard Visitor visa — apply via UKVI." },
  britain: { type: "visa_required", label: "UK visa required", action: "Ask if client has a valid UK Standard Visitor visa." },
  england: { type: "visa_required", label: "UK visa required", action: "Ask if client has a valid UK visa." },
  scotland: { type: "visa_required", label: "UK visa required", action: "Ask if client has a valid UK visa." },
  london: { type: "visa_required", label: "UK visa required", action: "UK visa required — apply via UKVI at least 3 weeks before travel." },
  manchester: { type: "visa_required", label: "UK visa required", action: "UK visa required — confirm client has applied via UKVI." },
  birmingham: { type: "visa_required", label: "UK visa required", action: "UK visa required." },
  edinburgh: { type: "visa_required", label: "UK visa required", action: "UK visa required." },

  // United States
  usa: { type: "visa_required", label: "US visa required", action: "Ask if client has a valid B1/B2 US visitor visa — DS-160 application required; allow 4–12 weeks for interview scheduling." },
  "united states": { type: "visa_required", label: "US visa required", action: "B1/B2 US visa required — DS-160 form + embassy interview needed." },
  america: { type: "visa_required", label: "US visa required", action: "US B1/B2 visa required — confirm client has completed DS-160 and attended embassy interview." },
  "new york": { type: "visa_required", label: "US visa required", action: "US visa required — B1/B2 visitor visa via US Embassy Harare." },
  "los angeles": { type: "visa_required", label: "US visa required", action: "US visa required." },
  chicago: { type: "visa_required", label: "US visa required", action: "US visa required." },
  miami: { type: "visa_required", label: "US visa required", action: "US visa required." },
  houston: { type: "visa_required", label: "US visa required", action: "US visa required." },
  dallas: { type: "visa_required", label: "US visa required", action: "US visa required." },
  "san francisco": { type: "visa_required", label: "US visa required", action: "US visa required." },
  atlanta: { type: "visa_required", label: "US visa required", action: "US visa required." },
  boston: { type: "visa_required", label: "US visa required", action: "US visa required." },
  seattle: { type: "visa_required", label: "US visa required", action: "US visa required." },
  washington: { type: "visa_required", label: "US visa required", action: "US visa required." },

  // Canada
  canada: { type: "visa_required", label: "Canadian visa required", action: "Ask if client has a Temporary Resident Visa (TRV) for Canada — apply online via IRCC. Processing 2–8 weeks." },
  toronto: { type: "visa_required", label: "Canadian visa required", action: "Canada TRV required — apply via IRCC online." },
  vancouver: { type: "visa_required", label: "Canadian visa required", action: "Canada TRV required." },
  montreal: { type: "visa_required", label: "Canadian visa required", action: "Canada TRV required." },
  calgary: { type: "visa_required", label: "Canadian visa required", action: "Canada TRV required." },

  // Schengen Area (apply at the main destination country embassy)
  europe: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa required — apply at the main destination country's embassy. Allow 2–4 weeks." },
  schengen: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa required." },
  france: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa required — apply at the French Embassy Harare. Allow 2–3 weeks." },
  paris: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (France) required." },
  nice: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (France) required." },
  lyon: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (France) required." },
  germany: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa required — apply at the German Embassy Harare." },
  berlin: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Germany) required." },
  frankfurt: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Germany) required." },
  munich: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Germany) required." },
  hamburg: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Germany) required." },
  netherlands: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa required — apply at the Dutch Embassy." },
  amsterdam: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Netherlands) required." },
  italy: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa required — apply at the Italian Embassy." },
  rome: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Italy) required." },
  milan: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Italy) required." },
  venice: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Italy) required." },
  spain: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa required — apply at the Spanish Embassy." },
  madrid: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Spain) required." },
  barcelona: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Spain) required." },
  portugal: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa required — apply at the Portuguese Embassy." },
  lisbon: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Portugal) required." },
  switzerland: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa required — apply at the Swiss Embassy." },
  zurich: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Switzerland) required." },
  geneva: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Switzerland) required." },
  austria: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa required — apply at the Austrian Embassy." },
  vienna: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Austria) required." },
  belgium: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa required — apply at the Belgian Embassy." },
  brussels: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Belgium) required." },
  sweden: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa required — apply at the Swedish Embassy." },
  stockholm: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Sweden) required." },
  norway: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa required — apply at the Norwegian Embassy." },
  oslo: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Norway) required." },
  denmark: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa required — apply at the Danish Embassy." },
  copenhagen: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Denmark) required." },
  finland: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa required." },
  helsinki: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Finland) required." },
  greece: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa required — apply at the Greek Embassy." },
  athens: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Greece) required." },
  poland: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa required." },
  warsaw: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Poland) required." },
  krakow: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Poland) required." },
  "czech republic": { type: "visa_required", label: "Schengen visa required", action: "Schengen visa required." },
  prague: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Czech Republic) required." },
  hungary: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa required." },
  budapest: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Hungary) required." },
  luxembourg: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Luxembourg) required." },
  malta: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Malta) required." },
  slovakia: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Slovakia) required." },
  bratislava: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Slovakia) required." },
  slovenia: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Slovenia) required." },
  ljubljana: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Slovenia) required." },
  iceland: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Iceland) required." },

  // Non-Schengen Europe (visa required)
  ireland: { type: "visa_required", label: "Irish visa required", action: "Irish visa required — apply at the Irish Embassy. Note: Schengen visa does NOT cover Ireland." },
  dublin: { type: "visa_required", label: "Irish visa required", action: "Irish visa required — not covered by Schengen visa." },
  russia: { type: "visa_required", label: "Russian visa required", action: "Russian visa required — apply at the Russian Embassy. Allow 2–4 weeks. Confirm current travel advisories." },
  moscow: { type: "visa_required", label: "Russian visa required", action: "Russian visa required." },
  ukraine: { type: "visa_required", label: "Ukrainian visa required", action: "Ukrainian visa required — confirm current travel advisories before booking." },
  kyiv: { type: "visa_required", label: "Ukrainian visa required", action: "Ukrainian visa required — confirm current travel advisories." },
  serbia: { type: "visa_required", label: "Serbian visa required", action: "Zimbabwean passport requires a visa for Serbia — confirm at the Serbian Embassy." },
  belgrade: { type: "visa_required", label: "Serbian visa required", action: "Serbian visa required." },
  romania: { type: "visa_required", label: "Romanian visa required", action: "Romanian visa required — Romania is not yet full Schengen for air travel; confirm current status." },
  bucharest: { type: "visa_required", label: "Romanian visa required", action: "Romanian visa required." },
  bulgaria: { type: "visa_required", label: "Bulgarian visa required", action: "Bulgarian visa required — Bulgaria is not full Schengen; confirm at Bulgarian Embassy." },
  sofia: { type: "visa_required", label: "Bulgarian visa required", action: "Bulgarian visa required." },
  croatia: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Croatia) required — Croatia joined Schengen Jan 2023." },
  zagreb: { type: "visa_required", label: "Schengen visa required", action: "Schengen visa (Croatia) required." },

  // Australia / New Zealand
  australia: { type: "visa_required", label: "Australian visa required", action: "Australian Tourist Visa (subclass 600) required — apply online at immi.homeaffairs.gov.au. Allow 4–8 weeks." },
  sydney: { type: "visa_required", label: "Australian visa required", action: "Australian Tourist Visa required — apply online at immi.homeaffairs.gov.au." },
  melbourne: { type: "visa_required", label: "Australian visa required", action: "Australian Tourist Visa required." },
  brisbane: { type: "visa_required", label: "Australian visa required", action: "Australian Tourist Visa required." },
  perth: { type: "visa_required", label: "Australian visa required", action: "Australian Tourist Visa required." },
  "new zealand": { type: "visa_required", label: "NZ visa required", action: "New Zealand visitor visa required — apply via Immigration NZ online. Allow 4–6 weeks." },
  auckland: { type: "visa_required", label: "NZ visa required", action: "NZ visitor visa required — apply via Immigration NZ." },
  wellington: { type: "visa_required", label: "NZ visa required", action: "NZ visitor visa required." },
  christchurch: { type: "visa_required", label: "NZ visa required", action: "NZ visitor visa required." },

  // Asia — visa required
  japan: { type: "visa_required", label: "Japanese visa required", action: "Japanese visa required — apply at the Japanese Embassy Harare. Allow 2–3 weeks. Bank statements and letter of invitation may be required." },
  tokyo: { type: "visa_required", label: "Japanese visa required", action: "Japanese visa required — apply at the Japanese Embassy." },
  osaka: { type: "visa_required", label: "Japanese visa required", action: "Japanese visa required." },
  china: { type: "visa_required", label: "Chinese visa required", action: "Chinese visa required — apply at the Chinese Embassy Harare. Tourist (L) or business (M) visa. Allow 2–4 weeks." },
  beijing: { type: "visa_required", label: "Chinese visa required", action: "Chinese visa required — apply at the Chinese Embassy." },
  shanghai: { type: "visa_required", label: "Chinese visa required", action: "Chinese visa required." },
  guangzhou: { type: "visa_required", label: "Chinese visa required", action: "Chinese visa required." },
  "hong kong": { type: "visa_required", label: "HK visa required", action: "Hong Kong visa required for Zimbabwean passport holders — apply via HKID. Note: separate from China visa." },
  "south korea": { type: "visa_required", label: "South Korean visa required", action: "South Korean visa required — apply at the South Korean Embassy. Allow 3–5 working days." },
  seoul: { type: "visa_required", label: "South Korean visa required", action: "South Korean visa required." },
  taiwan: { type: "visa_required", label: "Taiwan visa required", action: "Taiwan visa required — apply at the Taipei Economic and Cultural Office." },
  taipei: { type: "visa_required", label: "Taiwan visa required", action: "Taiwan visa required." },
  singapore: { type: "visa_required", label: "Singapore visa required", action: "Singapore visa required for Zimbabwean passport holders — apply via ICA (ica.gov.sg) or Singapore Embassy." },
  nigeria: { type: "visa_required", label: "Nigerian visa required", action: "Nigerian visa required — apply via Nigeria Immigration NIS portal or VFS Global." },
  lagos: { type: "visa_required", label: "Nigerian visa required", action: "Nigerian visa required." },
  abuja: { type: "visa_required", label: "Nigerian visa required", action: "Nigerian visa required." },
  ghana: { type: "visa_required", label: "Ghanaian visa required", action: "Ghanaian visa required — apply at the Ghanaian Embassy or via Ghana Immigration." },
  accra: { type: "visa_required", label: "Ghanaian visa required", action: "Ghanaian visa required." },
  pakistan: { type: "visa_required", label: "Pakistani visa required", action: "Pakistani visa required — apply at the Pakistani Embassy. Letter of invitation recommended." },
  karachi: { type: "visa_required", label: "Pakistani visa required", action: "Pakistani visa required." },
  lahore: { type: "visa_required", label: "Pakistani visa required", action: "Pakistani visa required." },
  islamabad: { type: "visa_required", label: "Pakistani visa required", action: "Pakistani visa required." },

  // Americas — visa required
  brazil: { type: "visa_required", label: "Brazilian visa required", action: "Brazilian visa required — apply at the Brazilian Embassy. Tourist visa or e-visa may be available." },
  "sao paulo": { type: "visa_required", label: "Brazilian visa required", action: "Brazilian visa required." },
  "rio de janeiro": { type: "visa_required", label: "Brazilian visa required", action: "Brazilian visa required." },
  argentina: { type: "visa_required", label: "Argentine visa required", action: "Argentine visa required — apply at the Argentine Embassy." },
  "buenos aires": { type: "visa_required", label: "Argentine visa required", action: "Argentine visa required." },
  mexico: { type: "visa_required", label: "Mexican visa required", action: "Mexican visa required for Zimbabwean passport — apply at the Mexican Embassy." },
  "mexico city": { type: "visa_required", label: "Mexican visa required", action: "Mexican visa required." },
  colombia: { type: "visa_required", label: "Colombian visa required", action: "Colombian visa required — apply at the Colombian Embassy." },
  bogota: { type: "visa_required", label: "Colombian visa required", action: "Colombian visa required." },
  peru: { type: "visa_required", label: "Peruvian visa required", action: "Peruvian visa required." },
  lima: { type: "visa_required", label: "Peruvian visa required", action: "Peruvian visa required." },
  chile: { type: "visa_required", label: "Chilean visa required", action: "Chilean visa required." },
  santiago: { type: "visa_required", label: "Chilean visa required", action: "Chilean visa required." },

  // North Africa — visa required
  morocco: { type: "visa_required", label: "Moroccan visa required", action: "Moroccan visa required for Zimbabwean passport — apply at the Moroccan Embassy." },
  casablanca: { type: "visa_required", label: "Moroccan visa required", action: "Moroccan visa required." },
  marrakech: { type: "visa_required", label: "Moroccan visa required", action: "Moroccan visa required." },
  algeria: { type: "visa_required", label: "Algerian visa required", action: "Algerian visa required — apply at the Algerian Embassy." },
  algiers: { type: "visa_required", label: "Algerian visa required", action: "Algerian visa required." },
  libya: { type: "visa_required", label: "Libyan visa required", action: "Libyan visa required — confirm current travel advisories before booking." },
  tripoli: { type: "visa_required", label: "Libyan visa required", action: "Libyan visa required — check current travel advisories." },

  // West Africa
  senegal: { type: "visa_required", label: "Senegalese visa required", action: "Senegalese visa required — apply at the Senegalese Embassy." },
  dakar: { type: "visa_required", label: "Senegalese visa required", action: "Senegalese visa required." },
  cameroon: { type: "visa_required", label: "Cameroonian visa required", action: "Cameroonian visa required — apply at the Cameroonian Embassy." },
  douala: { type: "visa_required", label: "Cameroonian visa required", action: "Cameroonian visa required." },
  yaounde: { type: "visa_required", label: "Cameroonian visa required", action: "Cameroonian visa required." },

  // ═══════════════════════════════════════════════════════════════════════
  // E-VISA (apply online before travel)
  // ═══════════════════════════════════════════════════════════════════════

  india: { type: "e_visa", label: "India e-visa required", action: "India e-visa required — apply at indianvisaonline.gov.in at least 4 days before travel. 30-day tourist e-visa available." },
  delhi: { type: "e_visa", label: "India e-visa required", action: "India e-visa required — apply at indianvisaonline.gov.in." },
  mumbai: { type: "e_visa", label: "India e-visa required", action: "India e-visa required — apply at indianvisaonline.gov.in." },
  bangalore: { type: "e_visa", label: "India e-visa required", action: "India e-visa required." },
  kolkata: { type: "e_visa", label: "India e-visa required", action: "India e-visa required." },
  chennai: { type: "e_visa", label: "India e-visa required", action: "India e-visa required." },
  turkey: { type: "e_visa", label: "Turkey e-visa required", action: "Turkey e-visa required — apply at evisa.gov.tr. Valid for 30 days, can be obtained same-day." },
  istanbul: { type: "e_visa", label: "Turkey e-visa required", action: "Turkey e-visa required — apply at evisa.gov.tr before travel." },
  ankara: { type: "e_visa", label: "Turkey e-visa required", action: "Turkey e-visa required." },
  "sri lanka": { type: "e_visa", label: "Sri Lanka e-visa required", action: "Sri Lanka ETA required — apply at eta.gov.lk. USD 35 fee for tourist ETA." },
  colombo: { type: "e_visa", label: "Sri Lanka e-visa required", action: "Sri Lanka ETA required — apply at eta.gov.lk." },
  vietnam: { type: "e_visa", label: "Vietnam e-visa required", action: "Vietnam e-visa required — apply at evisa.xuatnhapcanh.gov.vn. 90-day single entry e-visa available." },
  hanoi: { type: "e_visa", label: "Vietnam e-visa required", action: "Vietnam e-visa required." },
  "ho chi minh city": { type: "e_visa", label: "Vietnam e-visa required", action: "Vietnam e-visa required." },
  cambodia: { type: "e_visa", label: "Cambodia e-visa required", action: "Cambodia e-visa required — apply at evisa.gov.kh. USD 30 fee, 30-day single entry." },
  "phnom penh": { type: "e_visa", label: "Cambodia e-visa required", action: "Cambodia e-visa required." },
  "siem reap": { type: "e_visa", label: "Cambodia e-visa required", action: "Cambodia e-visa required." },
  myanmar: { type: "e_visa", label: "Myanmar e-visa required", action: "Myanmar e-visa required — apply at evisa.moip.gov.mm. Confirm current travel advisories." },
  yangon: { type: "e_visa", label: "Myanmar e-visa required", action: "Myanmar e-visa required — check current travel advisories." },
  "saudi arabia": { type: "e_visa", label: "Saudi Arabia visa required", action: "Saudi Arabia tourist e-visa available at visa.visitsaudi.com (for leisure travel). Business/work visa requires sponsor." },
  riyadh: { type: "e_visa", label: "Saudi Arabia visa required", action: "Saudi Arabia e-visa or sponsored visa required." },
  jeddah: { type: "e_visa", label: "Saudi Arabia visa required", action: "Saudi Arabia e-visa required." },
  medina: { type: "e_visa", label: "Saudi Arabia visa required", action: "Saudi Arabia visa required — Umrah/Hajj requires a specific religious visa from a licensed operator." },
  mecca: { type: "e_visa", label: "Saudi Arabia visa required", action: "Saudi Arabia visa required — Umrah/Hajj pilgrims need a religious visa from an approved operator." },
  oman: { type: "e_visa", label: "Oman e-visa required", action: "Oman e-visa required — apply at evisa.rop.gov.om. Multiple visa options available." },
  muscat: { type: "e_visa", label: "Oman e-visa required", action: "Oman e-visa required — apply at evisa.rop.gov.om." },
  bahrain: { type: "e_visa", label: "Bahrain visa required", action: "Bahrain visa-on-arrival or e-visa available — apply at evisa.nbr.gov.bh before travel." },
  manama: { type: "e_visa", label: "Bahrain visa required", action: "Bahrain e-visa required — apply at evisa.nbr.gov.bh." },
  kuwait: { type: "e_visa", label: "Kuwait visa required", action: "Kuwait e-visa required — apply via Kuwait's e-Visa portal. Sponsorship from a Kuwaiti national may be required." },
  "kuwait city": { type: "e_visa", label: "Kuwait visa required", action: "Kuwait e-visa required." },
  jordan: { type: "e_visa", label: "Jordan visa required", action: "Jordan e-visa or visa on arrival — apply at jordan.com/visa. Jordan Pass includes visa fee for tourists visiting attractions." },
  amman: { type: "e_visa", label: "Jordan visa required", action: "Jordan e-visa or visa on arrival available." },
  azerbaijan: { type: "e_visa", label: "Azerbaijan e-visa required", action: "Azerbaijan e-visa required — apply at evisa.gov.az. Processing 3–5 days." },
  baku: { type: "e_visa", label: "Azerbaijan e-visa required", action: "Azerbaijan e-visa required — apply at evisa.gov.az." },
  georgia: { type: "e_visa", label: "Georgia e-visa required", action: "Georgia e-visa required — apply at evisa.gov.ge." },
  tbilisi: { type: "e_visa", label: "Georgia e-visa required", action: "Georgia e-visa required." },

  // ═══════════════════════════════════════════════════════════════════════
  // ETA (Electronic Travel Authorization)
  // ═══════════════════════════════════════════════════════════════════════

  kenya: { type: "eta_required", label: "Kenya ETA required", action: "Kenya ETA required (replaced visa on arrival) — apply at etakenya.go.ke. USD 30 fee, process at least 3 days before travel." },
  nairobi: { type: "eta_required", label: "Kenya ETA required", action: "Kenya ETA required — apply at etakenya.go.ke before travel." },
  mombasa: { type: "eta_required", label: "Kenya ETA required", action: "Kenya ETA required — apply at etakenya.go.ke." },

  // ═══════════════════════════════════════════════════════════════════════
  // VISA ON ARRIVAL
  // ═══════════════════════════════════════════════════════════════════════

  uae: { type: "visa_on_arrival", label: "Visa on arrival (UAE)", action: "Visa on arrival for Zimbabwe — confirm passport has 6+ months validity. Free 30-day VoA at Dubai/Abu Dhabi airports." },
  dubai: { type: "visa_on_arrival", label: "Visa on arrival (Dubai)", action: "Visa on arrival at Dubai airport — ensure passport has 6+ months validity. Free 30-day stay." },
  "abu dhabi": { type: "visa_on_arrival", label: "Visa on arrival (UAE)", action: "Visa on arrival at Abu Dhabi airport — 6+ months passport validity required." },
  sharjah: { type: "visa_on_arrival", label: "Visa on arrival (UAE)", action: "Visa on arrival (UAE) — 6+ months passport validity required." },
  qatar: { type: "visa_on_arrival", label: "Visa on arrival (Qatar)", action: "Visa on arrival in Doha — confirm 6+ months passport validity. 30-day free VoA available." },
  doha: { type: "visa_on_arrival", label: "Visa on arrival (Doha)", action: "Visa on arrival at Hamad International Airport — 30-day free VoA for Zimbabwe passport." },
  egypt: { type: "visa_on_arrival", label: "Visa on arrival (Egypt)", action: "Visa on arrival at Cairo and other Egyptian airports — USD 25 fee. Confirm 6+ months passport validity." },
  cairo: { type: "visa_on_arrival", label: "Visa on arrival (Egypt)", action: "Visa on arrival at Cairo airport — USD 25 fee required." },
  luxor: { type: "visa_on_arrival", label: "Visa on arrival (Egypt)", action: "Visa on arrival (Egypt) — USD 25." },
  aswan: { type: "visa_on_arrival", label: "Visa on arrival (Egypt)", action: "Visa on arrival (Egypt) — USD 25." },
  "sharm el sheikh": { type: "visa_on_arrival", label: "Visa on arrival (Egypt)", action: "Visa on arrival at Sharm el-Sheikh — USD 25." },
  tanzania: { type: "visa_on_arrival", label: "Visa on arrival (Tanzania)", action: "Visa on arrival at Julius Nyerere Airport, Kilimanjaro, and other ports — USD 50 single entry." },
  "dar es salaam": { type: "visa_on_arrival", label: "Visa on arrival (Tanzania)", action: "Visa on arrival at Julius Nyerere Airport — USD 50." },
  zanzibar: { type: "visa_on_arrival", label: "Visa on arrival (Zanzibar)", action: "Visa on arrival in Zanzibar — USD 50 (same as Tanzania mainland)." },
  kilimanjaro: { type: "visa_on_arrival", label: "Visa on arrival (Tanzania)", action: "Visa on arrival at Kilimanjaro Airport — USD 50." },
  arusha: { type: "visa_on_arrival", label: "Visa on arrival (Tanzania)", action: "Visa on arrival (Tanzania) — USD 50." },
  ethiopia: { type: "visa_on_arrival", label: "Visa on arrival (Ethiopia)", action: "Visa on arrival at Addis Ababa Bole International Airport — USD 50. Also available via e-visa at evisa.gov.et." },
  "addis ababa": { type: "visa_on_arrival", label: "Visa on arrival (Ethiopia)", action: "Visa on arrival or e-visa at Bole Airport — USD 50." },
  thailand: { type: "visa_on_arrival", label: "Visa on arrival (Thailand)", action: "Visa on arrival at Bangkok Suvarnabhumi and other major airports — THB 2,000 (approx USD 55). Max 15 days." },
  bangkok: { type: "visa_on_arrival", label: "Visa on arrival (Thailand)", action: "Visa on arrival at Suvarnabhumi Airport — THB 2,000. Max 15-day stay." },
  "chiang mai": { type: "visa_on_arrival", label: "Visa on arrival (Thailand)", action: "Visa on arrival (Thailand) — THB 2,000." },
  phuket: { type: "visa_on_arrival", label: "Visa on arrival (Thailand)", action: "Visa on arrival at Phuket Airport — THB 2,000." },
  mozambique: { type: "visa_on_arrival", label: "Visa on arrival (Mozambique)", action: "Visa on arrival at Maputo and other Mozambican ports — USD 50 single entry. 30-day stay." },
  maputo: { type: "visa_on_arrival", label: "Visa on arrival (Mozambique)", action: "Visa on arrival at Maputo International Airport — USD 50." },
  beira: { type: "visa_on_arrival", label: "Visa on arrival (Mozambique)", action: "Visa on arrival at Beira — USD 50." },
  nepal: { type: "visa_on_arrival", label: "Visa on arrival (Nepal)", action: "Visa on arrival at Tribhuvan Airport, Kathmandu — USD 30 (15 days) or USD 50 (30 days)." },
  kathmandu: { type: "visa_on_arrival", label: "Visa on arrival (Nepal)", action: "Visa on arrival at Tribhuvan Airport — USD 30 for 15 days." },
  maldives: { type: "visa_on_arrival", label: "Visa on arrival (Maldives)", action: "Free visa on arrival in the Maldives — 30 days, no fee. Confirm 6+ months passport validity and return ticket." },
  male: { type: "visa_on_arrival", label: "Visa on arrival (Maldives)", action: "Free visa on arrival — 30 days. No fee required." },
  seychelles: { type: "visa_on_arrival", label: "Visa on arrival (Seychelles)", action: "Visitor's permit on arrival in Seychelles — free, 1-month stay. Confirm return ticket and accommodation." },
  "mahe": { type: "visa_on_arrival", label: "Visa on arrival (Seychelles)", action: "Visitor's permit on arrival — free, 1 month." },
  indonesia: { type: "visa_on_arrival", label: "Visa on arrival (Indonesia)", action: "Visa on arrival at Bali and other major Indonesian airports — USD 35. Max 30 days." },
  bali: { type: "visa_on_arrival", label: "Visa on arrival (Bali)", action: "Visa on arrival at Ngurah Rai Airport — USD 35. 30-day stay." },
  jakarta: { type: "visa_on_arrival", label: "Visa on arrival (Indonesia)", action: "Visa on arrival at Soekarno-Hatta Airport — USD 35." },
  madagascar: { type: "visa_on_arrival", label: "Visa on arrival (Madagascar)", action: "Visa on arrival at Antananarivo — free for 30 days or USD 35 for 90 days." },
  antananarivo: { type: "visa_on_arrival", label: "Visa on arrival (Madagascar)", action: "Visa on arrival at Ivato Airport — free for 30 days." },
  comoros: { type: "visa_on_arrival", label: "Visa on arrival (Comoros)", action: "Visa on arrival in the Comoros Islands — free, 45 days." },
  fiji: { type: "visa_on_arrival", label: "Visa on arrival (Fiji)", action: "Visa on arrival in Fiji — free, 4-month stay for most Commonwealth passport holders including Zimbabwe." },
  nadi: { type: "visa_on_arrival", label: "Visa on arrival (Fiji)", action: "Visa on arrival at Nadi Airport — free, up to 4 months." },

  // ═══════════════════════════════════════════════════════════════════════
  // VISA FREE (for Zimbabwe passport holders)
  // ═══════════════════════════════════════════════════════════════════════

  "south africa": { type: "visa_free", label: "Visa free (South Africa)", action: null },
  johannesburg: { type: "visa_free", label: "Visa free (South Africa)", action: null },
  "cape town": { type: "visa_free", label: "Visa free (South Africa)", action: null },
  durban: { type: "visa_free", label: "Visa free (South Africa)", action: null },
  pretoria: { type: "visa_free", label: "Visa free (South Africa)", action: null },
  botswana: { type: "visa_free", label: "Visa free (Botswana)", action: null },
  gaborone: { type: "visa_free", label: "Visa free (Botswana)", action: null },
  zambia: { type: "visa_free", label: "Visa free (Zambia)", action: null },
  lusaka: { type: "visa_free", label: "Visa free (Zambia)", action: null },
  livingstone: { type: "visa_free", label: "Visa free (Zambia)", action: null },
  namibia: { type: "visa_free", label: "Visa free (Namibia)", action: null },
  windhoek: { type: "visa_free", label: "Visa free (Namibia)", action: null },
  malawi: { type: "visa_free", label: "Visa free (Malawi)", action: null },
  lilongwe: { type: "visa_free", label: "Visa free (Malawi)", action: null },
  blantyre: { type: "visa_free", label: "Visa free (Malawi)", action: null },
  eswatini: { type: "visa_free", label: "Visa free (Eswatini)", action: null },
  swaziland: { type: "visa_free", label: "Visa free (Eswatini)", action: null },
  mbabane: { type: "visa_free", label: "Visa free (Eswatini)", action: null },
  lesotho: { type: "visa_free", label: "Visa free (Lesotho)", action: null },
  maseru: { type: "visa_free", label: "Visa free (Lesotho)", action: null },
  rwanda: { type: "visa_free", label: "Visa free (Rwanda) — 30 days", action: null },
  kigali: { type: "visa_free", label: "Visa free (Rwanda) — 30 days", action: null },
  mauritius: { type: "visa_free", label: "Visa free (Mauritius) — up to 90 days", action: null },
  "port louis": { type: "visa_free", label: "Visa free (Mauritius) — up to 90 days", action: null },
  malaysia: { type: "visa_free", label: "Visa free (Malaysia) — up to 30 days", action: null },
  "kuala lumpur": { type: "visa_free", label: "Visa free (Malaysia) — up to 30 days", action: null },
  angola: { type: "visa_free", label: "Visa free (Angola)", action: null },
  luanda: { type: "visa_free", label: "Visa free (Angola)", action: null },
  "cape verde": { type: "visa_free", label: "Visa on arrival (Cape Verde) — free", action: "Visa on arrival at Santiago Airport — free entry for most nationalities including Zimbabwe." },
  praia: { type: "visa_free", label: "Visa on arrival (Cape Verde)", action: "Visa on arrival at Santiago Airport — free." },
};

const CITY_ALIASES = [
  // ── Zimbabwe ─────────────────────────────────────────────────────────────────
  { name: "Harare",           aliases: ["harare", "hre"] },
  { name: "Bulawayo",         aliases: ["bulawayo", "byo"] },
  { name: "Victoria Falls",   aliases: ["victoria falls", "vic falls", "vfa"] },
  { name: "Mutare",           aliases: ["mutare", "umtali"] },
  { name: "Gweru",            aliases: ["gweru", "gwelo"] },
  { name: "Masvingo",         aliases: ["masvingo"] },
  // ── Southern Africa ──────────────────────────────────────────────────────────
  { name: "Johannesburg",     aliases: ["johannesburg", "joburg", "jhb", "o.r. tambo", "jnb", "or tambo"] },
  { name: "Cape Town",        aliases: ["cape town", "capetown", "cpt"] },
  { name: "Durban",           aliases: ["durban", "dbn"] },
  { name: "Pretoria",         aliases: ["pretoria", "tshwane"] },
  { name: "Port Elizabeth",   aliases: ["port elizabeth", "gqeberha", "plz"] },
  { name: "Lusaka",           aliases: ["lusaka", "lun"] },
  { name: "Livingstone",      aliases: ["livingstone", "liv"] },
  { name: "Ndola",            aliases: ["ndola", "nla"] },
  { name: "Gaborone",         aliases: ["gaborone", "gbe"] },
  { name: "Windhoek",         aliases: ["windhoek", "wdh"] },
  { name: "Maputo",           aliases: ["maputo", "mpm"] },
  { name: "Beira",            aliases: ["beira", "bew"] },
  { name: "Lilongwe",         aliases: ["lilongwe", "llw"] },
  { name: "Blantyre",         aliases: ["blantyre", "blz"] },
  { name: "Mbabane",          aliases: ["mbabane", "mts"] },
  { name: "Maseru",           aliases: ["maseru", "msu"] },
  { name: "Antananarivo",     aliases: ["antananarivo", "tana", "tnr"] },
  // ── East Africa ──────────────────────────────────────────────────────────────
  { name: "Nairobi",          aliases: ["nairobi", "nbo", "jkia"] },
  { name: "Mombasa",          aliases: ["mombasa", "mom"] },
  { name: "Dar es Salaam",    aliases: ["dar es salaam", "dar", "dares salaam", "dss"] },
  { name: "Zanzibar",         aliases: ["zanzibar", "znz"] },
  { name: "Kigali",           aliases: ["kigali", "kgl"] },
  { name: "Kampala",          aliases: ["kampala", "entebbe", "ebb"] },
  { name: "Addis Ababa",      aliases: ["addis ababa", "addis", "add"] },
  { name: "Djibouti",         aliases: ["djibouti", "jib"] },
  { name: "Asmara",           aliases: ["asmara", "asm"] },
  { name: "Juba",             aliases: ["juba", "jub"] },
  { name: "Khartoum",         aliases: ["khartoum", "khartum", "krt"] },
  { name: "Bujumbura",        aliases: ["bujumbura", "bjm"] },
  // ── West Africa ──────────────────────────────────────────────────────────────
  { name: "Lagos",            aliases: ["lagos", "los"] },
  { name: "Accra",            aliases: ["accra", "acc"] },
  { name: "Abuja",            aliases: ["abuja", "abv"] },
  { name: "Dakar",            aliases: ["dakar", "dkr"] },
  { name: "Abidjan",          aliases: ["abidjan", "abj"] },
  { name: "Douala",           aliases: ["douala", "dla"] },
  { name: "Lomé",             aliases: ["lome", "lomé", "lfw"] },
  { name: "Cotonou",          aliases: ["cotonou", "coo"] },
  { name: "Bamako",           aliases: ["bamako", "bko"] },
  { name: "Ouagadougou",      aliases: ["ouagadougou", "oua"] },
  { name: "Freetown",         aliases: ["freetown", "fna"] },
  { name: "Monrovia",         aliases: ["monrovia", "rob"] },
  { name: "Conakry",          aliases: ["conakry", "cky"] },
  { name: "Accra",            aliases: ["accra", "acc"] },
  { name: "Kinshasa",         aliases: ["kinshasa", "fih"] },
  { name: "Luanda",           aliases: ["luanda", "lad"] },
  { name: "Brazzaville",      aliases: ["brazzaville", "bzv"] },
  { name: "Libreville",       aliases: ["libreville", "lbv"] },
  { name: "Yaoundé",          aliases: ["yaounde", "yaoundé", "yao"] },
  // ── North Africa ─────────────────────────────────────────────────────────────
  { name: "Cairo",            aliases: ["cairo", "cai"] },
  { name: "Casablanca",       aliases: ["casablanca", "cmn"] },
  { name: "Marrakech",        aliases: ["marrakech", "marrakesh", "rak"] },
  { name: "Tunis",            aliases: ["tunis", "tun"] },
  { name: "Tripoli",          aliases: ["tripoli", "tip"] },
  { name: "Algiers",          aliases: ["algiers", "alger", "alg"] },
  { name: "Alexandria",       aliases: ["alexandria", "hbe"] },
  // ── Middle East ──────────────────────────────────────────────────────────────
  { name: "Dubai",            aliases: ["dubai", "dxb"] },
  { name: "Abu Dhabi",        aliases: ["abu dhabi", "auh"] },
  { name: "Doha",             aliases: ["doha", "doh"] },
  { name: "Riyadh",           aliases: ["riyadh", "ruh"] },
  { name: "Jeddah",           aliases: ["jeddah", "jedda", "jed"] },
  { name: "Medina",           aliases: ["medina", "med", "madinah"] },
  { name: "Kuwait City",      aliases: ["kuwait city", "kuwait", "kwi"] },
  { name: "Manama",           aliases: ["manama", "bahrain", "bah"] },
  { name: "Muscat",           aliases: ["muscat", "mct"] },
  { name: "Amman",            aliases: ["amman", "amm"] },
  { name: "Beirut",           aliases: ["beirut", "bei"] },
  { name: "Tel Aviv",         aliases: ["tel aviv", "tlv"] },
  { name: "Istanbul",         aliases: ["istanbul", "ist"] },
  { name: "Ankara",           aliases: ["ankara", "esb"] },
  { name: "Sanaa",            aliases: ["sanaa", "sana'a", "sah"] },
  // ── Asia — South ─────────────────────────────────────────────────────────────
  { name: "Delhi",            aliases: ["delhi", "new delhi", "del"] },
  { name: "Mumbai",           aliases: ["mumbai", "bombay", "bom"] },
  { name: "Bangalore",        aliases: ["bangalore", "bengaluru", "blr"] },
  { name: "Chennai",          aliases: ["chennai", "madras", "maa"] },
  { name: "Hyderabad",        aliases: ["hyderabad", "hyd"] },
  { name: "Kolkata",          aliases: ["kolkata", "calcutta", "ccu"] },
  { name: "Islamabad",        aliases: ["islamabad", "isb"] },
  { name: "Karachi",          aliases: ["karachi", "khi"] },
  { name: "Lahore",           aliases: ["lahore", "lhe"] },
  { name: "Colombo",          aliases: ["colombo", "cmb"] },
  { name: "Dhaka",            aliases: ["dhaka", "dac"] },
  { name: "Kathmandu",        aliases: ["kathmandu", "ktm"] },
  // ── Asia — Southeast ─────────────────────────────────────────────────────────
  { name: "Kuala Lumpur",     aliases: ["kuala lumpur", "kl", "klia", "kul"] },
  { name: "Singapore",        aliases: ["singapore", "sin", "sng"] },
  { name: "Bangkok",          aliases: ["bangkok", "bkk"] },
  { name: "Phuket",           aliases: ["phuket", "hkt"] },
  { name: "Chiang Mai",       aliases: ["chiang mai", "cnx"] },
  { name: "Yangon",           aliases: ["yangon", "rangoon", "rgn"] },
  { name: "Phnom Penh",       aliases: ["phnom penh", "pnh"] },
  { name: "Siem Reap",        aliases: ["siem reap", "rep"] },
  { name: "Ho Chi Minh City", aliases: ["ho chi minh city", "ho chi minh", "saigon", "sgn"] },
  { name: "Hanoi",            aliases: ["hanoi", "han"] },
  { name: "Danang",           aliases: ["da nang", "danang", "dad"] },
  { name: "Jakarta",          aliases: ["jakarta", "cgk"] },
  { name: "Bali",             aliases: ["bali", "denpasar", "dps"] },
  { name: "Surabaya",         aliases: ["surabaya", "sub"] },
  { name: "Manila",           aliases: ["manila", "mnl"] },
  { name: "Cebu",             aliases: ["cebu", "ceb"] },
  { name: "Vientiane",        aliases: ["vientiane", "vte"] },
  // ── Asia — East ──────────────────────────────────────────────────────────────
  { name: "Beijing",          aliases: ["beijing", "peking", "pek"] },
  { name: "Shanghai",         aliases: ["shanghai", "pvg", "sha"] },
  { name: "Guangzhou",        aliases: ["guangzhou", "canton", "can"] },
  { name: "Shenzhen",         aliases: ["shenzhen", "szx"] },
  { name: "Chengdu",          aliases: ["chengdu", "ctu"] },
  { name: "Hong Kong",        aliases: ["hong kong", "hkg"] },
  { name: "Macau",            aliases: ["macau", "mfm"] },
  { name: "Taipei",           aliases: ["taipei", "tpe"] },
  { name: "Tokyo",            aliases: ["tokyo", "nrt", "hnd"] },
  { name: "Osaka",            aliases: ["osaka", "kix"] },
  { name: "Seoul",            aliases: ["seoul", "icn", "gmp"] },
  { name: "Busan",            aliases: ["busan", "pus"] },
  // ── Asia — Central ───────────────────────────────────────────────────────────
  { name: "Tashkent",         aliases: ["tashkent", "tas"] },
  { name: "Almaty",           aliases: ["almaty", "alma-ata", "alc"] },
  { name: "Nur-Sultan",       aliases: ["nur-sultan", "astana", "tsj"] },
  { name: "Baku",             aliases: ["baku", "gyd"] },
  { name: "Tbilisi",          aliases: ["tbilisi", "tbs"] },
  { name: "Yerevan",          aliases: ["yerevan", "evn"] },
  { name: "Ulaanbaatar",      aliases: ["ulaanbaatar", "ulan bator", "uln"] },
  // ── Europe — Western ─────────────────────────────────────────────────────────
  { name: "London",           aliases: ["london", "lhr", "lgw", "heathrow", "gatwick", "stansted", "stn"] },
  { name: "Paris",            aliases: ["paris", "cdg", "ory"] },
  { name: "Amsterdam",        aliases: ["amsterdam", "ams"] },
  { name: "Brussels",         aliases: ["brussels", "bru"] },
  { name: "Frankfurt",        aliases: ["frankfurt", "fra"] },
  { name: "Munich",           aliases: ["munich", "muc"] },
  { name: "Hamburg",          aliases: ["hamburg", "ham"] },
  { name: "Berlin",           aliases: ["berlin", "ber", "txl", "sxf"] },
  { name: "Zurich",           aliases: ["zurich", "zürich", "zrh"] },
  { name: "Geneva",           aliases: ["geneva", "geneve", "gva"] },
  { name: "Vienna",           aliases: ["vienna", "vie"] },
  { name: "Rome",             aliases: ["rome", "fco"] },
  { name: "Milan",            aliases: ["milan", "mxp", "lin", "linate", "malpensa"] },
  { name: "Venice",           aliases: ["venice", "vce"] },
  { name: "Barcelona",        aliases: ["barcelona", "bcn"] },
  { name: "Madrid",           aliases: ["madrid", "mad"] },
  { name: "Lisbon",           aliases: ["lisbon", "lis"] },
  // ── Europe — Northern ────────────────────────────────────────────────────────
  { name: "Stockholm",        aliases: ["stockholm", "arn", "arlanda"] },
  { name: "Copenhagen",       aliases: ["copenhagen", "cph"] },
  { name: "Oslo",             aliases: ["oslo", "osl"] },
  { name: "Helsinki",         aliases: ["helsinki", "hel"] },
  { name: "Dublin",           aliases: ["dublin", "dub"] },
  // ── Europe — Eastern / Central ───────────────────────────────────────────────
  { name: "Warsaw",           aliases: ["warsaw", "waw"] },
  { name: "Prague",           aliases: ["prague", "prg"] },
  { name: "Budapest",         aliases: ["budapest", "bud"] },
  { name: "Bucharest",        aliases: ["bucharest", "otm"] },
  { name: "Sofia",            aliases: ["sofia", "sof"] },
  { name: "Athens",           aliases: ["athens", "ath"] },
  { name: "Kyiv",             aliases: ["kyiv", "kiev", "iev", "bor"] },
  { name: "Moscow",           aliases: ["moscow", "svo", "dme"] },
  { name: "St Petersburg",    aliases: ["st petersburg", "saint petersburg", "led"] },
  { name: "Riga",             aliases: ["riga", "rix"] },
  { name: "Tallinn",          aliases: ["tallinn", "tll"] },
  { name: "Vilnius",          aliases: ["vilnius", "vno"] },
  { name: "Bratislava",       aliases: ["bratislava", "bts"] },
  { name: "Zagreb",           aliases: ["zagreb", "zag"] },
  { name: "Belgrade",         aliases: ["belgrade", "beg"] },
  // ── Americas — North ─────────────────────────────────────────────────────────
  { name: "New York",         aliases: ["new york", "jfk", "lga", "ewr"] },
  { name: "Los Angeles",      aliases: ["los angeles", "lax"] },
  { name: "Chicago",          aliases: ["chicago", "ord", "midway"] },
  { name: "Miami",            aliases: ["miami", "mia"] },
  { name: "Houston",          aliases: ["houston", "iah", "hou"] },
  { name: "Dallas",           aliases: ["dallas", "dfw", "dal"] },
  { name: "San Francisco",    aliases: ["san francisco", "sfo"] },
  { name: "Seattle",          aliases: ["seattle", "sea"] },
  { name: "Boston",           aliases: ["boston", "bos"] },
  { name: "Atlanta",          aliases: ["atlanta", "atl"] },
  { name: "Washington",       aliases: ["washington dc", "washington", "iad", "dca"] },
  { name: "Las Vegas",        aliases: ["las vegas", "las", "las vegas nv"] },
  { name: "Orlando",          aliases: ["orlando", "mco"] },
  { name: "Toronto",          aliases: ["toronto", "yyz"] },
  { name: "Vancouver",        aliases: ["vancouver", "yvr"] },
  { name: "Montreal",         aliases: ["montreal", "yul"] },
  { name: "Calgary",          aliases: ["calgary", "yyc"] },
  { name: "Mexico City",      aliases: ["mexico city", "cdmx", "mex"] },
  { name: "Cancun",           aliases: ["cancun", "cun"] },
  // ── Americas — Central & Caribbean ───────────────────────────────────────────
  { name: "Panama City",      aliases: ["panama city", "pty"] },
  { name: "San José",         aliases: ["san jose costa rica", "san josé", "sjo"] },
  { name: "Havana",           aliases: ["havana", "la habana", "hav"] },
  // ── Americas — South ─────────────────────────────────────────────────────────
  { name: "São Paulo",        aliases: ["sao paulo", "são paulo", "gru", "guarulhos"] },
  { name: "Rio de Janeiro",   aliases: ["rio de janeiro", "rio", "gig", "sdu"] },
  { name: "Buenos Aires",     aliases: ["buenos aires", "eze", "aep"] },
  { name: "Santiago",         aliases: ["santiago", "scl"] },
  { name: "Lima",             aliases: ["lima", "lim"] },
  { name: "Bogotá",           aliases: ["bogota", "bogotá", "bog"] },
  { name: "Medellín",         aliases: ["medellin", "medellín", "mde"] },
  { name: "Quito",            aliases: ["quito", "uio"] },
  { name: "Caracas",          aliases: ["caracas", "ccs"] },
  // ── Oceania ───────────────────────────────────────────────────────────────────
  { name: "Sydney",           aliases: ["sydney", "syd"] },
  { name: "Melbourne",        aliases: ["melbourne", "mel"] },
  { name: "Brisbane",         aliases: ["brisbane", "bne"] },
  { name: "Perth",            aliases: ["perth", "per"] },
  { name: "Adelaide",         aliases: ["adelaide", "adl"] },
  { name: "Auckland",         aliases: ["auckland", "akl"] },
  { name: "Wellington",       aliases: ["wellington", "wlg"] },
  { name: "Christchurch",     aliases: ["christchurch", "chc"] },
  { name: "Nadi",             aliases: ["nadi", "suva", "nan"] },
];

// ── Suppression patterns — when these are present in customer or outbound ──────
// messages the conversation is about an existing booking / active dialogue.
// Quote detection should be muted to avoid false positives.
const SUPPRESSION_PATTERNS = [
  // Existing booking confirmation signals
  "booking reference", "booking ref", "pnr", "e-ticket", "eticket",
  "ticket number", "reservation number", "confirmation number", "confirmation code",
  "your booking is confirmed", "booking has been confirmed", "order number",
  "as per your booking", "as per the booking", "please find your itinerary",
  "attached your itinerary", "your itinerary is", "your e-ticket", "attached e-ticket",
  "travel documents attached",
  // Active-dialogue / follow-up signals
  "as discussed", "as per our conversation", "as per our discussion",
  "following up on", "just checking in", "just to confirm",
  "further to our", "in reference to", "in response to",
  "per our email", "per my last email", "as i mentioned", "as mentioned earlier",
  "to recap", "here is the quote", "here is your quote", "please find the quote",
  "attached quote", "quote attached", "see attached", "as requested",
  "as per your request", "please see below",
];

// ── Intent rules with tiered keywords ─────────────────────────────────────────
// Each rule now has:
//   strong  — one match = high confidence (2+ points), intent fires immediately
//   medium  — one match = moderate signal (1 point); need 2+ points total to fire
//   minScore — minimum score to classify (default 1, stronger intents require 2)
// NOTE: Order matters — specific service types must come before flight_booking.
const INTENT_RULES = [
  {
    key: "refund_request",
    label: "Refund Request",
    specialist: "Ticketing",
    minScore: 1,
    strong: ["refund", "money back", "chargeback", "cancel and refund", "get my money back"],
    medium: ["cancel", "cancellation"],
  },
  {
    key: "ticket_change",
    label: "Ticket Change",
    specialist: "Ticketing",
    minScore: 1,
    strong: ["reschedule", "change my flight", "change flight", "change ticket", "change booking",
             "move my flight", "move ticket", "postpone my flight", "bring forward"],
    medium: ["postpone", "rebook", "amendment", "amend"],
  },
  // Specific service types checked BEFORE flight_booking ──────────────────────
  {
    key: "visa_support",
    label: "Visa Support",
    specialist: "Documentation",
    minScore: 1,
    strong: ["visa application", "apply for a visa", "visa support", "visa requirements",
             "invitation letter", "study visa", "tourist visa", "transit visa",
             "work permit", "residence permit", "travel document"],
    medium: ["visa", "embassy", "passport", "permit"],
  },
  {
    key: "hotel_booking",
    label: "Hotel Booking",
    specialist: "Accommodation",
    minScore: 2,
    strong: ["book a hotel", "book a room", "hotel booking", "accommodation booking",
             "need a hotel", "looking for a hotel", "hotel in", "accommodation in"],
    medium: ["hotel", "accommodation", "guesthouse", "lodge", "airbnb",
             "bed and breakfast", "villa", "resort", "self-catering", "apartment",
             "suite", "check in", "check out", "nights"],
  },
  {
    key: "airport_transfer",
    label: "Airport Transfer",
    specialist: "Transfers",
    minScore: 1,
    strong: ["airport transfer", "airport pickup", "airport pick up", "airport drop",
             "pickup from airport", "pick up from airport", "drop off at airport",
             "collect from airport", "airport shuttle", "airport taxi"],
    medium: [],
  },
  {
    key: "car_hire",
    label: "Car Hire",
    specialist: "Transfers",
    minScore: 1,
    strong: ["car hire", "car rental", "rent a car", "hire a car", "vehicle hire",
             "rental car", "self-drive", "hire car"],
    medium: [],
  },
  {
    key: "tour_package",
    label: "Tour Package",
    specialist: "Tours",
    minScore: 2,
    strong: ["tour package", "book a tour", "tour and accommodation", "safari tour",
             "day tour", "guided tour"],
    medium: ["tour", "package", "safari", "excursion", "holiday package"],
  },
  // Flight booking — only matches if no specific service was detected above ───
  // Requires a stronger signal to avoid false positives on general chatter.
  {
    key: "flight_booking",
    label: "Flight Booking",
    specialist: "Ticketing",
    minScore: 2,
    strong: ["book a flight", "flight quote", "flight booking", "air ticket",
             "flight prices", "price for a flight", "quote for a flight",
             "flights from", "flying from", "return ticket", "one way ticket",
             "need tickets", "need flights", "book flights", "flight from",
             "please quote", "kindly quote", "quotation for", "requesting quotation",
             "request for quotation", "rfq"],
    medium: ["flight", "flights", "ticket", "tickets", "departure", "arrival",
             "one way", "round trip", "economy", "business class",
             "passenger", "traveller", "traveler"],
  },
  {
    key: "payment_issue",
    label: "Payment Issue",
    specialist: "Finance",
    minScore: 1,
    strong: ["payment not received", "payment failed", "proof of payment", "transfer proof",
             "payment reference", "not reflecting", "paid but"],
    medium: ["payment", "paid", "invoice", "receipt", "transaction"],
  },
  {
    key: "complaint",
    label: "Service Complaint",
    specialist: "Supervisor",
    minScore: 1,
    strong: ["bad service", "complain", "complaint", "frustrated", "unhappy",
             "terrible", "worst", "speak to a manager", "speak to supervisor"],
    medium: ["delay", "delayed", "late", "manager", "supervisor"],
  },
];

const POSITIVE_WORDS = [
  "thanks",
  "thank you",
  "great",
  "awesome",
  "perfect",
  "appreciate",
  "good",
  "helpful",
  "amazing",
];

const NEGATIVE_WORDS = [
  "delay",
  "delayed",
  "refund",
  "cancel",
  "angry",
  "frustrated",
  "upset",
  "worried",
  "problem",
  "issue",
  "slow",
  "terrible",
  "bad",
  "worst",
  "complaint",
  "manager",
];

const URGENT_WORDS = [
  "urgent",
  "asap",
  "today",
  "tonight",
  "tomorrow",
  "next",
  "immediately",
  "now",
  "missed my flight",
  "stranded",
];

const containsAny = (text, keywords = []) =>
  keywords.some((keyword) => text.includes(keyword));

const titleCase = (value) =>
  String(value || "")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const normalize = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^\w\s/.-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const TEAM_EMAIL_TOKEN = "abisiniya";

const extractEmails = (value) =>
  String(value || "")
    .match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi)
    ?.map((email) => String(email || "").toLowerCase())
    ?.filter(Boolean) || [];

const containsTeamEmail = (value) =>
  extractEmails(value).some((email) => email.includes(TEAM_EMAIL_TOKEN));

const isTeamEmail = (value) =>
  extractEmails(value).some((email) => email.includes(TEAM_EMAIL_TOKEN));

const stripQuotedTeamContent = (value) => {
  const text = String(value || "").replace(/\r\n?/g, "\n");
  if (!text.trim()) return "";

  const lines = text.split("\n");
  for (let i = 0; i < lines.length; i += 1) {
    const line = String(lines[i] || "").trim().toLowerCase();
    const windowText = lines.slice(i, i + 6).join("\n");
    const looksLikeQuotedHeader =
      line.startsWith("from:")
      || line.startsWith("reply-to:")
      || /^on .+ wrote:$/.test(line);
    const looksLikeDivider =
      line.includes("original message")
      || line.includes("forwarded message")
      || line.includes("gmail_quote");

    if (looksLikeQuotedHeader && containsTeamEmail(lines[i])) {
      return lines.slice(0, i).join("\n").trim();
    }

    if (looksLikeDivider && containsTeamEmail(windowText)) {
      return lines.slice(0, i).join("\n").trim();
    }
  }

  return text.trim();
};

const isExternalMessage = (message) =>
  !["system", "internal_note"].includes(String(message?.message_type || "").toLowerCase());

const isActualTeamReplyMessage = (message) =>
  isExternalMessage(message)
  && (message?.direction === "outbound" || isTeamEmail(message?.sender?.email));

const hasTeamReplyEvidenceInBody = (message) =>
  isExternalMessage(message) && containsTeamEmail(message?.body?.text || "");

const isCustomerInboundMessage = (message) =>
  isExternalMessage(message)
  && message?.direction === "inbound"
  && !isTeamEmail(message?.sender?.email)
  && Boolean(stripQuotedTeamContent(message?.body?.text || ""));

const sanitizeCustomerMessage = (message) => ({
  ...message,
  body: {
    ...(message?.body || {}),
    text: stripQuotedTeamContent(message?.body?.text || ""),
  },
});

const getCustomerMessages = (messages = []) =>
  messages
    .filter((message) => isCustomerInboundMessage(message))
    .map((message) => sanitizeCustomerMessage(message));

// Score a single intent rule against normalized text.
// Returns { rule, score } — score >= rule.minScore means the intent fires.
const scoreIntentRule = (text, rule) => {
  let score = 0;
  for (const kw of (rule.strong || [])) {
    if (text.includes(kw)) { score += 2; break; } // one strong match is enough
  }
  for (const kw of (rule.medium || [])) {
    if (text.includes(kw)) { score += 1; }
  }
  return score;
};

const detectIntent = (text) => {
  for (const rule of INTENT_RULES) {
    const score = scoreIntentRule(text, rule);
    if (score >= (rule.minScore ?? 1)) {
      return { ...rule, confidence: Math.min(1, score / 4) };
    }
  }
  return { key: "general_inquiry", label: "General Inquiry", specialist: "Front Desk", confidence: 0 };
};

// Check if the text contains suppression patterns (existing booking / active dialogue)
const detectSuppression = (text) => {
  const hits = SUPPRESSION_PATTERNS.filter((p) => text.includes(p));
  return { suppressed: hits.length >= 2, hits, softSuppressed: hits.length === 1 };
};

const detectSentiment = (text) => {
  const positiveHits = POSITIVE_WORDS.filter((word) => text.includes(word)).length;
  const negativeHits = NEGATIVE_WORDS.filter((word) => text.includes(word)).length;
  const strongNegative =
    negativeHits >= 2 ||
    containsAny(text, ["worst", "ridiculous", "manager", "supervisor", "refund now"]);

  if (strongNegative || negativeHits > positiveHits) {
    return {
      label: "Negative",
      tone: "negative",
      color: "#D24B4B",
      icon: MoodBadRoundedIcon,
      strong: strongNegative,
    };
  }

  if (positiveHits > negativeHits && positiveHits > 0) {
    return {
      label: "Positive",
      tone: "positive",
      color: "#208A52",
      icon: SentimentSatisfiedAltRoundedIcon,
      strong: false,
    };
  }

  return {
    label: "Neutral",
    tone: "neutral",
    color: "#5F7386",
    icon: SentimentSatisfiedAltRoundedIcon,
    strong: false,
  };
};

const detectPassengerCount = (text) => {
  const wordList = Object.keys(WORD_NUMBERS).join("|");
  const toInt = (raw) => WORD_NUMBERS[raw.toLowerCase()] ?? Number(raw);

  // "Number of passengers: 3"
  const labelMatch = text.match(
    /number\s+of\s+(?:people|passengers?|persons?|travellers?|pax)\s*[:-]?\s*(\d{1,2})\b/i
  );
  if (labelMatch) return Number(labelMatch[1]);

  // "family of 4" / "group of six" / "party of 3"
  const groupMatch = text.match(
    new RegExp(`\\b(?:family|group|party|team)\\s+of\\s+(\\d{1,2}|${wordList})\\b`, "i")
  );
  if (groupMatch) return toInt(groupMatch[1]);

  // "2 adults and 1 child" — sum both
  const adultMatch = text.match(new RegExp(`\\b(\\d{1,2}|${wordList})\\s+adults?\\b`, "i"));
  const childMatch = text.match(new RegExp(`\\b(\\d{1,2}|${wordList})\\s+(?:children|child|kids?|infants?)\\b`, "i"));
  if (adultMatch && childMatch) return toInt(adultMatch[1]) + toInt(childMatch[1]);

  // "myself and my wife" / "me and my husband"
  if (/\b(?:my(?:self)?|me)\s+and\s+(?:my\s+)?(?:wife|husband|partner|colleague|friend)\b/i.test(text)) return 2;

  // "3 passengers" / "three people"
  const digitMatch = text.match(
    /(\d{1,2})\s*(?:passengers?|people|persons?|travellers?|travelers?|adults?|tickets?|seats?|pax)\b/
  );
  if (digitMatch) return Number(digitMatch[1]);

  const wordMatch = text.match(
    new RegExp(`\\b(${wordList})\\s+(?:passengers?|people|persons?|travellers?|travelers?|adults?|tickets?|seats?|pax)\\b`, "i")
  );
  if (wordMatch) return toInt(wordMatch[1]);

  return null;
};

const detectTripType = (text) => {
  // Require explicit trip-type phrases — bare "return" is too common in non-travel contexts
  if (containsAny(text, ["return trip", "return ticket", "return flight", "return fare", "round trip", "roundtrip", "two way", "two-way"])) {
    return "Return";
  }
  if (containsAny(text, ["one way", "one-way"])) {
    return "One-way";
  }
  return null;
};

const detectTravelClass = (text) => {
  // Bare "business" and bare "first" are intentionally excluded —
  // they fire on "business trip", "first available", "first time booking" etc.
  if (containsAny(text, ["business class", "biz class"])) return "Business";
  if (containsAny(text, ["premium economy"])) return "Premium Economy";
  if (containsAny(text, ["first class"])) return "First";
  if (containsAny(text, ["economy class", "economy"])) return "Economy";
  return null;
};

const detectBudget = (rawText) => {
  const text = String(rawText || "");

  // Explicit currency codes / $ symbol
  const codeMatch = text.match(/(?:usd|zar|zwl|\$)\s?\d[\d,]*(?:\.\d+)?/i);
  if (codeMatch) return codeMatch[0].toUpperCase();

  // "R" prefix only when followed by 2+ digits (avoids "rate 15", "room 3", "ref 12345")
  const zarMatch = text.match(/\bR\s?(\d{2}[\d,]*(?:\.\d+)?)\b/);
  if (zarMatch) return `R${zarMatch[1]}`;

  // "budget of 2000" / "up to 3000" / "around $1500"
  const budgetCtxMatch = text.match(/\b(?:budget|up to|around|approximately|about)\s+(?:usd|zar|zwl|\$|R)?\s?(\d[\d,]*(?:\.\d+)?)/i);
  if (budgetCtxMatch) return budgetCtxMatch[0].trim().toUpperCase();

  // "1500 per person"
  const perPersonMatch = text.match(/(\d[\d,]*(?:\.\d+)?)\s+per\s+(?:person|head|pax)/i);
  if (perPersonMatch) return `${perPersonMatch[1]} per person`;

  return null;
};

const detectReturnDate = (rawText) => {
  const value = String(rawText || "");

  // "returning on/by", "back on/by", "coming back on"
  const contextMatch = value.match(
    /(?:returning|back|coming back)\s+(?:on|by)\s+([^\n,]{5,50})/i
  );
  if (contextMatch) {
    const d = detectTravelDate(contextMatch[1]);
    if (d) return d;
  }

  // "Return Date: ..."
  const labelMatch = value.match(/return\s+date[^:]*:\s*([^\n]{5,50})/i);
  if (labelMatch) {
    const d = detectTravelDate(labelMatch[1]);
    if (d) return d;
  }

  // Date range: "26 March - 5 April" or "26 March to 5 April" → take second date
  const rangeMatch = value.match(
    /\b\d{1,2}(?:st|nd|rd|th)?\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*(?:\s+\d{4})?\s*[-–—]|to\s+(\d{1,2}(?:st|nd|rd|th)?\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*(?:\s+\d{4})?)\b/i
  );
  if (rangeMatch && rangeMatch[1]) {
    const d = detectTravelDate(rangeMatch[1]);
    if (d) return d;
  }

  // Two-sided range: "26 March - 5 April" capture both, return second
  const twoSideMatch = value.match(
    /\b\d{1,2}(?:st|nd|rd|th)?\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*(?:\s+\d{4})?\s*[-–—to]+\s*(\d{1,2}(?:st|nd|rd|th)?\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*(?:\s+\d{4})?)\b/i
  );
  if (twoSideMatch && twoSideMatch[1]) {
    const d = detectTravelDate(twoSideMatch[1]);
    if (d) return d;
  }

  return null;
};

const detectQuotationCount = (text) => {
  // "2 quotations", "3 different quotations"
  const numericMatch = text.match(/(\d{1,2})\s+(?:different\s+)?quotations?\b/i);
  if (numericMatch) {
    const n = parseInt(numericMatch[1], 10);
    if (n >= 2 && n <= 20) return n;
  }

  // "two quotations", "three different quotes"
  const wordMatch = text.match(
    /\b(two|three|four|five|six|seven|eight|nine|ten)\s+(?:different\s+)?quotations?\b/i
  );
  if (wordMatch) {
    return WORD_NUMBERS[wordMatch[1].toLowerCase()] || null;
  }

  // Count "Quotation 1:", "Quotation 2:", etc.
  const blocks = (text.match(/(?:Quotation|Quote)\s+\d+\s*[:-]/gi) || []).length;
  if (blocks >= 2) return blocks;

  return null;
};

const detectVisaRequirement = (text) => {
  const lower = text.toLowerCase();
  // Longest key first so "south africa" matches before "africa"
  const keys = Object.keys(VISA_DB).sort((a, b) => b.length - a.length);
  for (const key of keys) {
    if (lower.includes(key)) {
      return { ...VISA_DB[key], destination: key.charAt(0).toUpperCase() + key.slice(1) };
    }
  }
  return null;
};

// Returns ALL service intents detected in text (used for multi-service display).
// Requires score >= minScore (same threshold as primary detection) to avoid noise.
const SKIP_MULTI_SERVICE = new Set(["general_inquiry", "payment_issue", "complaint"]);
const detectAllServiceIntents = (text) =>
  INTENT_RULES.filter((rule) => {
    if (SKIP_MULTI_SERVICE.has(rule.key)) return false;
    return scoreIntentRule(text, rule) >= (rule.minScore ?? 1);
  });

/**
 * Parse "Quotation N:" / "Quote N:" blocks from raw text.
 * Returns an array of per-block objects (destination, route, pax count, names, dates).
 */
const detectMultipleQuotes = (rawText) => {
  const text = String(rawText || "");
  const blockRx = /(?:Quotation|Quote)\s+(\d+)\s*[:-]/gi;
  const positions = [];
  let m;
  while ((m = blockRx.exec(text)) !== null) {
    positions.push({ index: m.index + m[0].length, number: parseInt(m[1], 10) });
  }
  if (positions.length < 2) return [];

  return positions.map((pos, i) => {
    const blockText = text.slice(pos.index, positions[i + 1]?.index ?? text.length);
    const lower = blockText.toLowerCase();

    // Destination
    const destMatch = blockText.match(/destination\s*[-:]\s*([^\n,]{3,50})/i);
    const destination = destMatch ? destMatch[1].trim() : null;

    // Route: "from Harare to Accra" / "from X to Y" or just the destination
    const routeMatch = blockText.match(/from\s+([\w\s]+?)\s+to\s+([\w\s]+?)(?:\s*,|\s*:|\s*\n|\s*$)/i);
    const routeFrom = routeMatch ? routeMatch[1].trim() : "Harare";
    const routeTo   = routeMatch ? routeMatch[2].trim() : (destination || null);
    const routeLabel = routeFrom && routeTo ? `${routeFrom} → ${routeTo}` : (destination || null);

    // Number of people — "Number of people: 3" or "3 people"
    const paxLabelMatch = blockText.match(/number\s+of\s+(?:people|passengers?|persons?)\s*[:-]?\s*(\d{1,2})/i);
    const paxDigitMatch = blockText.match(/(\d{1,2})\s*(?:passengers?|people|persons?|travellers?)/i);
    const passengerCount = paxLabelMatch
      ? parseInt(paxLabelMatch[1], 10)
      : paxDigitMatch ? parseInt(paxDigitMatch[1], 10) : null;

    // Traveller names — cascading formats (stops at first format that yields results)
    const nameLines = [];
    const TITLE = "(?:Mr\\.?|Mrs\\.?|Ms\\.?|Miss\\.?|Dr\\.?|Prof\\.?|Hon\\.?|Honourable)";
    const CLASS_PAT = "(?:Business Class|Economy Class|First Class|Premium Economy|Biz Class)";

    // Format 1: "Mr Smith   Economy Class"
    const fmt1Rx = new RegExp(`^(${TITLE}[^\\n]{2,55}?)\\s{2,}(${CLASS_PAT})\\s*$`, "gim");
    let nm;
    while ((nm = fmt1Rx.exec(blockText)) !== null) {
      nameLines.push({ name: nm[1].trim(), travelClass: nm[2].trim() });
    }

    if (!nameLines.length) {
      // Format 2: numbered list "1. Mr John Smith" / "2) Jane Doe"
      const fmt2Rx = /^\s*\d{1,2}[.)]\s+(.{4,60})\s*$/gim;
      while ((nm = fmt2Rx.exec(blockText)) !== null) {
        const n = nm[1].trim();
        if (n.length >= 4 && !/destination|quotation|departure|date/i.test(n)) {
          nameLines.push({ name: n, travelClass: null });
        }
      }
    }

    if (!nameLines.length) {
      // Format 3: "PAX 1: Mr John Smith" / "Pax name: Mrs Jones"
      const fmt3Rx = /\bpax\s*(?:\d+\s*)?[:-]\s*(.{4,60})\b/gi;
      while ((nm = fmt3Rx.exec(blockText)) !== null) {
        nameLines.push({ name: nm[1].trim(), travelClass: null });
      }
    }

    if (!nameLines.length) {
      // Format 4: GDS "SMITH/JOHN MR"
      const fmt4Rx = /\b([A-Z]{2,20})\/([A-Z][A-Z\s]{1,20})\s+(?:MR|MRS|MS|DR)\b/g;
      while ((nm = fmt4Rx.exec(blockText)) !== null) {
        const surname = nm[1].charAt(0) + nm[1].slice(1).toLowerCase();
        const given   = nm[2].trim().split(" ").map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(" ");
        nameLines.push({ name: `${surname} ${given}`, travelClass: null });
      }
    }

    if (!nameLines.length) {
      // Format 5: title-prefixed lines alone (no class required)
      const fmt5Rx = new RegExp(`^${TITLE}\\s+[A-Z][a-zA-Z\\s.\\-]{2,50}\\s*$`, "gim");
      while ((nm = fmt5Rx.exec(blockText)) !== null) {
        const n = nm[0].trim();
        if (!/destination|quotation|departure/i.test(n)) {
          nameLines.push({ name: n, travelClass: null });
        }
      }
    }

    // Departure date
    const depMatch = blockText.match(/departure\s+date[^:\n]*:\s*([^\n]{5,50})/i);
    const departureDate = depMatch ? detectTravelDate(depMatch[1]) : null;

    // Return date
    const retMatch = blockText.match(/return\s+date[^:\n]*:\s*([^\n]{5,50})/i);
    const returnDateVal = retMatch ? detectTravelDate(retMatch[1]) : null;

    // Visa for this destination
    const visaForDest = destination ? detectVisaRequirement(lower) : null;

    return {
      number: pos.number,
      destination,
      routeLabel,
      routeFrom,
      routeTo,
      passengerCount: passengerCount ?? (nameLines.length > 0 ? nameLines.length : null),
      travellers: nameLines,
      departureDate,
      returnDate: returnDateVal,
      visa: visaForDest,
    };
  });
};

// ── Trip purpose detection ────────────────────────────────────────────────────
// Detects WHY the client is travelling so consultants can tailor advice.
// Notably, "on business" / "for business" must NOT be confused with "Business Class".
const TRIP_PURPOSE_RULES = [
  {
    key: "business",
    label: "Business Travel",
    patterns: [
      /\bfor\s+(?:a\s+)?business\b/i,
      /\bon\s+business\b/i,
      /\bbusiness\s+trip\b/i,
      /\bbusiness\s+meeting\b/i,
      /\bbusiness\s+travel\b/i,
      /\bwork\s+trip\b/i,
      /\bfor\s+work\b/i,
      /\bbusiness\s+conference\b/i,
      /\bwork\s+related\b/i,
      /\bofficial\s+trip\b/i,
      /\bofficial\s+business\b/i,
      /\bcorporate\s+travel\b/i,
    ],
    tip: "Business travellers may need flexible/changeable tickets. Ask about travel class preference and company travel policy.",
  },
  {
    key: "conference",
    label: "Conference / Event",
    patterns: [
      /\bconference\b/i,
      /\bsummit\b/i,
      /\bseminar\b/i,
      /\bworkshop\b/i,
      /\bexpo\b/i,
      /\bexhibition\b/i,
      /\bfor\s+(?:a\s+)?(?:conference|summit|seminar|workshop|expo)\b/i,
    ],
    tip: "Conference travellers often have fixed dates. Confirm exact dates and whether accommodation is also needed.",
  },
  {
    key: "leisure",
    label: "Leisure / Holiday",
    patterns: [
      /\bholiday\b/i,
      /\bvacation\b/i,
      /\bleisure\b/i,
      /\btourism\b/i,
      /\bsightseeing\b/i,
      /\bfor\s+(?:fun|pleasure|rest|holiday)\b/i,
      /\bfamily\s+(?:holiday|vacation|trip)\b/i,
      /\bgetaway\b/i,
    ],
    tip: "Leisure travellers may value cheaper flexible fares. Ask about preferred activities and whether a tour package is needed.",
  },
  {
    key: "honeymoon",
    label: "Honeymoon",
    patterns: [/\bhoneymoon\b/i, /\bwedding\s+(?:trip|travel|anniversary)\b/i],
    tip: "Honeymoon travellers value romantic destinations and upgrades. Consider recommending premium economy or business class.",
  },
  {
    key: "medical",
    label: "Medical Travel",
    patterns: [
      /\bmedical\b/i,
      /\btreatment\b/i,
      /\bhospital\b/i,
      /\bsurgery\b/i,
      /\bhealth\b/i,
      /\bfor\s+(?:medical|treatment|surgery|hospital)\b/i,
      /\bmedical\s+appointment\b/i,
    ],
    tip: "Medical travellers may need open-ended return tickets. Confirm they have travel insurance and check if a letter of invitation is needed.",
  },
  {
    key: "study",
    label: "Study / Education",
    patterns: [
      /\bstudy\b/i,
      /\bstudent\b/i,
      /\buniversity\b/i,
      /\bcollege\b/i,
      /\bschool\b/i,
      /\bacademic\b/i,
      /\bfor\s+(?:studies|school|university|college)\b/i,
    ],
    tip: "Students may need a student visa in addition to a travel visa. Confirm their student status and institution letter.",
  },
  {
    key: "relocation",
    label: "Relocation",
    patterns: [
      /\brelocat\w+\b/i,
      /\bmoving\s+to\b/i,
      /\bsettl\w+\s+in\b/i,
      /\bpermanent\s+(?:move|residence)\b/i,
      /\bemigrat\w+\b/i,
    ],
    tip: "Relocation clients need one-way or open-ended tickets and may need help with excess baggage allowance.",
  },
  {
    key: "religious",
    label: "Religious / Pilgrimage",
    patterns: [
      /\bumrah\b/i,
      /\bhajj\b/i,
      /\bpilgrimage\b/i,
      /\bchurch\s+(?:trip|tour|conference)\b/i,
      /\breligious\s+(?:trip|travel|visit)\b/i,
    ],
    tip: "Pilgrimage travellers (Hajj/Umrah) require specific visa documentation. Confirm they have an approved Umrah/Hajj package from a licensed operator.",
  },
];

const detectTripPurpose = (text) => {
  for (const rule of TRIP_PURPOSE_RULES) {
    if (rule.patterns.some((p) => p.test(text))) {
      return { key: rule.key, label: rule.label, tip: rule.tip };
    }
  }
  return null;
};

// Build an alias → city name lookup (longest aliases first to avoid partial matches)
const ALIAS_LOOKUP = (() => {
  const entries = [];
  for (const city of CITY_ALIASES) {
    for (const alias of city.aliases) {
      entries.push([alias, city.name]);
    }
  }
  // Sort longest first so "new york" matches before "york"
  entries.sort((a, b) => b[0].length - a[0].length);
  return entries;
})();

const resolveCity = (fragment) => {
  const lower = String(fragment || "").toLowerCase();
  for (const [alias, name] of ALIAS_LOOKUP) {
    if (lower.includes(alias)) return name;
  }
  return null;
};

const findCitiesInOrder = (text) => {
  const hits = CITY_ALIASES.map((city) => {
    const positions = city.aliases
      .map((alias) => text.indexOf(alias))
      .filter((index) => index >= 0)
      .sort((a, b) => a - b);
    return positions.length ? { name: city.name, index: positions[0] } : null;
  }).filter(Boolean);

  return hits
    .sort((a, b) => a.index - b.index)
    .map((h) => h.name)
    .filter((c, i, arr) => arr.indexOf(c) === i);
};

// Intents that show a full origin → destination route pair
const ROUTE_DETECTING_INTENTS = new Set([
  "flight_booking", "ticket_change", "refund_request", "car_hire", "airport_transfer",
]);

// Intents where we show a single destination city (not an origin→destination pair)
const DESTINATION_ONLY_INTENTS = new Set(["hotel_booking", "tour_package"]);

/**
 * Detect route with directional parsing.
 *
 * Priority order:
 *  1. IATA slash notation "HRE/NBO/LHR"
 *  2. Directional "from X to Y" / "X to Y" / "departing X arriving Y" / "X - Y"
 *  3. Positional city order fallback
 */
const detectRoute = (text, intent) => {
  // Hotel/tour: destination city only
  if (DESTINATION_ONLY_INTENTS.has(intent?.key)) {
    const cities = findCitiesInOrder(text);
    return cities.find((c) => c !== "Harare") ?? cities[0] ?? null;
  }

  if (!ROUTE_DETECTING_INTENTS.has(intent?.key)) return null;

  // ── 1. IATA slash notation ────────────────────────────────────────────────
  const iataMatch = text.match(/\b([A-Z]{3})(?:\/[A-Z]{3}){1,4}\b/);
  if (iataMatch) {
    const codes   = iataMatch[0].split("/");
    const resolved = codes.map(resolveCity).filter(Boolean);
    if (resolved.length >= 2) {
      const from = resolved[0];
      const to   = resolved[resolved.length - 1];
      return resolved.length > 2 ? resolved.join(" → ") : `${from} -> ${to}`;
    }
  }

  // ── 2. Directional patterns ───────────────────────────────────────────────
  // Pattern A: "from [city] to [city]"
  const fromTo = text.match(/\bfrom\s+(.{3,30}?)\s+to\s+(.{3,30})(?:\s|$|,|\.)/i);
  if (fromTo) {
    const from = resolveCity(fromTo[1]);
    const to   = resolveCity(fromTo[2]);
    if (from && to && from !== to) return `${from} -> ${to}`;
  }

  // Pattern B: "departing [city]" … "arriving/destination [city]"
  const depMatch = text.match(/\bdeparting\s+(.{3,30})(?:\s|,|\.)/i);
  const arrMatch = text.match(/\b(?:arriving(?:\s+in)?|destination)\s+(.{3,30})(?:\s|,|\.)/i);
  if (depMatch && arrMatch) {
    const from = resolveCity(depMatch[1]);
    const to   = resolveCity(arrMatch[1]);
    if (from && to && from !== to) return `${from} -> ${to}`;
  }

  // Pattern C: "[city] to [city]" (without "from")
  const cityTo = text.match(/\b(.{3,25}?)\s+to\s+(.{3,25})(?:\s|$|,|\.)/i);
  if (cityTo) {
    const from = resolveCity(cityTo[1]);
    const to   = resolveCity(cityTo[2]);
    if (from && to && from !== to) return `${from} -> ${to}`;
  }

  // Pattern D: "city - city" / "city → city"
  const dashMatch = text.match(/\b(.{3,25}?)\s*[-–—→]\s*(.{3,25})(?:\s|$|,|\.)/i);
  if (dashMatch) {
    const from = resolveCity(dashMatch[1]);
    const to   = resolveCity(dashMatch[2]);
    if (from && to && from !== to) return `${from} -> ${to}`;
  }

  // ── 3. Positional fallback ────────────────────────────────────────────────
  const cities = findCitiesInOrder(text);
  if (cities.length >= 3) return cities.join(" → ");
  if (cities.length === 2) return `${cities[0]} -> ${cities[1]}`;
  if (cities.length === 1 && cities[0] !== "Harare") return `Harare -> ${cities[0]}`;
  return null;
};

const MONTH_NAMES_PATTERN = "jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?";

const detectTravelDate = (rawText) => {
  const value = String(rawText || "");

  // ── Context-aware: "departing/leaving/flying on 15 April" ────────────────
  // Strip the verb prefix so it doesn't interfere with date pattern matching
  const contextMatch = value.match(
    /\b(?:departing|leaving|flying|travelling?|departs?)\s+(?:on\s+)?(.{5,40})/i
  );
  if (contextMatch) {
    const inner = detectTravelDate(contextMatch[1]);
    if (inner) return inner;
  }

  // ── Approximate expressions: "early March", "mid-April", "end of June" ───
  const approxMatch = value.match(
    new RegExp(`\\b(early|mid(?:-?month)?|end\\s+of|late)\\s+(${MONTH_NAMES_PATTERN})(?:\\s+(\\d{4}))?\\b`, "i")
  );
  if (approxMatch) {
    const qualifier = approxMatch[1].toLowerCase().replace(/\s+/g, " ");
    const monthStr  = approxMatch[2];
    const yearStr   = approxMatch[3];
    return titleCase(`${qualifier} ${monthStr}${yearStr ? " " + yearStr : ""}`);
  }

  const patterns = [
    new RegExp(`\\bnext\\s+(${DAY_WORDS})\\b`, "i"),
    new RegExp(`\\bthis\\s+(${DAY_WORDS})\\b`, "i"),
    /\b(today|tonight|tomorrow|next weekend|this weekend|next week)\b/i,
    // Numeric DD/MM or DD/MM/YYYY — require plausible day (1-31) and month (1-12)
    /\b([0-2]?\d|3[01])[/-]([01]?\d|1[0-2])(?:[/-]\d{2,4})?\b/,
    // Day-first: "31 March 2026", "31st March"
    new RegExp(`\\b\\d{1,2}(?:st|nd|rd|th)?\\s+(?:${MONTH_NAMES_PATTERN})(?:\\s+\\d{4})?\\b`, "i"),
    // US month-first: "March 26, 2026" or "March 26th"
    new RegExp(`\\b(?:${MONTH_NAMES_PATTERN})\\.?\\s+\\d{1,2}(?:st|nd|rd|th)?(?:,\\s*\\d{4})?\\b`, "i"),
  ];

  const match = patterns
    .map((pattern) => value.match(pattern))
    .find((result) => result?.[0]);

  return match?.[0] ? titleCase(match[0]) : null;
};

const detectUrgency = (text, sentiment, extracted) => {
  const high =
    sentiment.strong ||
    containsAny(text, URGENT_WORDS) ||
    Boolean(extracted.travelDate && extracted.route);

  if (high) {
    return {
      label: "High",
      color: "#B54708",
      bg: "#FFF3E0",
    };
  }

  if (extracted.route || extracted.travelDate || extracted.passengerCount) {
    return {
      label: "Medium",
      color: "#1D4ED8",
      bg: "#EAF2FF",
    };
  }

  return {
    label: "Low",
    color: "#4B5563",
    bg: "#EEF2F6",
  };
};

const buildNextActions = ({ intent, sentiment, extracted }) => {
  const actions = [];

  if (sentiment.label === "Negative") {
    actions.push("Apologize and acknowledge the earlier frustration.");
  }

  // ── Trip purpose advisory ──────────────────────────────────────────────────
  if (extracted.tripPurpose) {
    actions.push(`${extracted.tripPurpose.label} detected — ${extracted.tripPurpose.tip}`);
  }

  // ── Proactive visa advisory — shown for ALL intents, not just flight quotes ─
  // Visa info is surfaced regardless of whether a full quote has been started,
  // because the client needs to know before committing to dates.
  if (extracted.visaRequirement) {
    const v = extracted.visaRequirement;
    if (v.type === "visa_required" && v.action) {
      actions.push(`⚠ Visa required: ${v.action}`);
    } else if (v.type === "e_visa" && v.action) {
      actions.push(`🖥 E-visa needed: ${v.action}`);
    } else if (v.type === "eta_required" && v.action) {
      actions.push(`📋 ETA required: ${v.action}`);
    } else if (v.type === "visa_on_arrival" && v.action) {
      actions.push(`🛂 Visa on arrival: ${v.action}`);
    }
    // visa_free — no action needed, don't clutter the list
  }

  switch (intent.key) {
    case "flight_booking":
      actions.push(
        extracted.travelDate
          ? extracted.returnDate
            ? `Outbound: ${extracted.travelDate} — Return: ${extracted.returnDate}. Confirm both are correct.`
            : "Confirm the exact outbound date and ask for the return date."
          : "Ask for the exact departure date and return date."
      );
      actions.push(
        extracted.route
          ? "Ask for the preferred departure time and travel class."
          : "Confirm the origin and destination route."
      );
      if (!extracted.passengerCount) {
        actions.push("Confirm how many passengers are travelling.");
      }
      if (extracted.multipleQuotes?.length >= 2) {
        const summary = extracted.multipleQuotes
          .map((q) => `Q${q.number}: ${q.destination || q.routeTo || "unknown"} (${q.passengerCount ?? q.travellers?.length ?? "?"} pax)`)
          .join(" | ");
        actions.push(`${extracted.multipleQuotes.length} quotations needed — ${summary}`);
        const visaDestinations = extracted.multipleQuotes
          .filter((q) => q.visa && q.visa.type === "visa_required")
          .map((q) => q.destination || q.routeTo);
        if (visaDestinations.length > 0) {
          actions.push(`Visa check required for: ${visaDestinations.join(", ")} — confirm clients have valid visas.`);
        }
      } else if (extracted.quotationCount && extracted.quotationCount >= 2) {
        actions.push(`Client needs ${extracted.quotationCount} separate quotations — prepare each route/set individually.`);
      }
      actions.push("Assign to ticketing.");
      break;
    case "ticket_change":
      actions.push("Ask for the booking reference and new preferred date.");
      actions.push("Confirm any fare difference before reissue.");
      actions.push("Assign to ticketing.");
      break;
    case "refund_request":
      actions.push("Confirm the booking reference and reason for refund.");
      actions.push("Explain the refund review steps and turnaround time.");
      actions.push("Assign to ticketing.");
      break;
    case "visa_support":
      actions.push("Ask which country the visa is for and the intended travel date.");
      actions.push("Confirm nationality and passport expiry — some destinations need 6 months validity.");
      actions.push("Ask whether they need tourist, business, transit, or work visa.");
      actions.push("Route to the documentation desk.");
      break;
    case "hotel_booking":
      if (!extracted.travelDate) {
        actions.push("Ask for the check-in date.");
      }
      actions.push("Confirm the check-out date and number of nights.");
      if (!extracted.passengerCount) {
        actions.push("Ask how many guests will be staying and if any are children.");
      }
      actions.push("Ask for preferred location, star rating, or budget per night.");
      actions.push("Route to accommodation support.");
      break;
    case "tour_package":
      actions.push("Confirm destination, travel dates, and number of travellers.");
      actions.push("Ask about interests — safari, beach, cultural, adventure, or mixed.");
      actions.push("Confirm budget range and whether flights are needed in the package.");
      actions.push("Route to tours.");
      break;
    case "car_hire":
      actions.push("Ask for the pick-up location, drop-off location, and rental dates.");
      if (!extracted.travelDate) {
        actions.push("Ask for the pick-up date and time.");
      }
      if (!extracted.passengerCount) {
        actions.push("Confirm the number of passengers to recommend the right vehicle type.");
      }
      actions.push("Route to the transfers desk for vehicle options and pricing.");
      break;
    case "airport_transfer":
      actions.push("Ask for the airport name and whether this is a pick-up or drop-off.");
      if (!extracted.travelDate) {
        actions.push("Ask for the transfer date and exact flight arrival or departure time.");
      }
      actions.push("Confirm the collection or drop-off address and number of passengers.");
      actions.push("Route to transfers.");
      break;
    case "payment_issue":
      actions.push("Ask for the payment reference and proof of payment.");
      actions.push("Route the chat to finance support.");
      break;
    case "complaint":
      actions.push("Summarize the issue clearly and reassure the customer.");
      actions.push("Escalate to a supervisor if the complaint continues.");
      break;
    default:
      actions.push("Clarify the customer's main request and ownership.");
      actions.push("Keep the chat with front desk until intent is confirmed.");
      break;
  }

  return actions.filter((action, index, allActions) => allActions.indexOf(action) === index).slice(0, 4);
};

const buildSummary = ({ intent, sentiment, extracted }) => {
  const details = [];

  if (extracted.passengerCount) {
    details.push(`${extracted.passengerCount} passenger${extracted.passengerCount > 1 ? "s" : ""}`);
  }
  if (extracted.tripType) {
    details.push(extracted.tripType.toLowerCase());
  }
  if (extracted.route) {
    details.push(extracted.route);
  }
  if (extracted.travelDate) {
    details.push(extracted.travelDate);
  }

  const base =
    intent.key === "general_inquiry"
      ? "Customer likely needs general support."
      : `Customer likely needs ${intent.label.toLowerCase()}.`;

  const detailText = details.length ? ` Signals: ${details.join(", ")}.` : "";
  const sentimentText =
    sentiment.label === "Negative"
      ? " They sound frustrated."
      : sentiment.label === "Positive"
        ? " They sound positive."
        : "";

  return `${base}${detailText}${sentimentText}`.trim();
};

export const buildConversationAnalysis = (conversation, messages = []) => {
  const allCustomerMessages = getCustomerMessages(messages);

  // Full transcript — used for sticky field detection so data is never lost as conversation grows
  const transcript = allCustomerMessages.map((message) => String(message?.body?.text || "")).join(" ");
  const normalizedTranscript = normalize(transcript);

  // Recent transcript (last 8) — used for intent + sentiment to avoid old context dominating
  const recentMessages = allCustomerMessages.slice(-8);
  const recentTranscript = recentMessages.map((message) => String(message?.body?.text || "")).join(" ");
  const normalizedRecentTranscript = normalize(recentTranscript);

  const latestCustomerText = allCustomerMessages[allCustomerMessages.length - 1]?.body?.text || "";
  const normalizedLatestText = normalize(latestCustomerText);

  // Intent / sentiment: recent window only
  const analysisText = [normalizedRecentTranscript, normalizedLatestText].filter(Boolean).join(" ");
  // Field detection: full conversation history (sticky)
  const fullAnalysisText = [normalizedTranscript, normalizedLatestText].filter(Boolean).join(" ");

  const suppression = detectSuppression(analysisText);
  const intent = detectIntent(analysisText);
  const sentiment = detectSentiment(analysisText);
  const route = detectRoute(fullAnalysisText, intent);
  const multipleQuotes = detectMultipleQuotes(transcript);

  // When multiple structured blocks are detected, aggregate their data for the snapshot
  let aggregateRoute = route;
  let aggregatePax = detectPassengerCount(fullAnalysisText);
  if (multipleQuotes.length >= 2) {
    const totalPax = multipleQuotes.reduce((sum, q) => sum + (q.passengerCount || 0), 0);
    if (totalPax > 0) aggregatePax = totalPax;
    // Show "Multiple (N)" for route when there are different destinations
    const destinations = [...new Set(multipleQuotes.map((q) => q.routeTo).filter(Boolean))];
    if (destinations.length >= 2) {
      aggregateRoute = `Multiple (${destinations.join(", ")})`;
    }
  }

  // For travel dates, prefer the most recent occurrence to avoid surfacing stale dates
  const recentDateText = recentMessages.map((m) => String(m?.body?.text || "")).join(" ");
  const travelDateValue  = detectTravelDate(recentDateText)  || detectTravelDate(transcript);
  const returnDateValue  = detectReturnDate(recentDateText)  || detectReturnDate(transcript);

  const extracted = {
    passengerCount: aggregatePax,
    tripType: detectTripType(fullAnalysisText),
    route: typeof aggregateRoute === "string" ? aggregateRoute : route,
    travelDate: multipleQuotes.length >= 2 ? (multipleQuotes[0]?.departureDate || travelDateValue) : travelDateValue,
    returnDate:  multipleQuotes.length >= 2 ? (multipleQuotes[0]?.returnDate   || returnDateValue) : returnDateValue,
    quotationCount: detectQuotationCount(fullAnalysisText) ?? (multipleQuotes.length >= 2 ? multipleQuotes.length : null),
    travelClass: detectTravelClass(fullAnalysisText),
    budget: detectBudget(transcript),
    visaRequirement: detectVisaRequirement(fullAnalysisText),
    // Trip purpose: scan full transcript (sticky — keeps context even after consultant replies)
    tripPurpose: detectTripPurpose(normalizedTranscript),
    multipleQuotes,
  };
  const urgency = detectUrgency(analysisText, sentiment, extracted);

  // A suppressed conversation should not be shown as a quote booking intent.
  // If suppressed, downgrade a booking intent to general_inquiry so the
  // predictive next action card doesn't show quote fields for an existing booking.
  const effectiveIntent = suppression.suppressed && ["flight_booking", "hotel_booking", "tour_package", "airport_transfer", "car_hire"].includes(intent.key)
    ? { key: "general_inquiry", label: "General Inquiry", specialist: "Front Desk", confidence: 0 }
    : intent;

  return {
    intent: effectiveIntent,
    rawIntent: intent,
    suppression,
    sentiment,
    urgency,
    extracted,
    nextActions: buildNextActions({ intent: effectiveIntent, sentiment, extracted }),
    summary: buildSummary({ intent: effectiveIntent, sentiment, extracted }),
    specialist: effectiveIntent.specialist,
    canAnalyze: allCustomerMessages.length > 0,
    customerName:
      conversation?.customer?.name ||
      conversation?.contact?.name ||
      "Customer",
  };
};

// ── Field-level conversation tracker ────────────────────────────────────────
// Scans ALL messages (both directions) to determine whether each quote field
// has been: (a) already answered by the customer, (b) asked by the consultant
// but not yet answered, or (c) not yet raised at all.
//
// States: 'answered' | 'asked' | 'needed'
const FIELD_ASK_PATTERNS = {
  route: [/from where/i, /which airport/i, /flying from/i, /departure.*city/i, /destination/i, /where.*to/i, /route/i, /which city/i, /origin/i],
  date:  [/travel date/i, /departure date/i, /when.*travel/i, /what date/i, /which date/i, /preferred date/i, /when.*flying/i, /when.*leaving/i],
  pax:   [/how many.*pass/i, /number of pass/i, /how many people/i, /how many travell/i, /how many person/i, /number of travell/i],
  class: [/cabin class/i, /class of travel/i, /economy or/i, /business or/i, /travel class/i, /what class/i, /which class/i, /preferred class/i],
  tripType: [/one.way or return/i, /return trip/i, /round trip/i, /one.way/i, /returning/i],
  visa:  [/passport/i, /visa/i, /travel document/i, /valid passport/i, /visa requirement/i],
};

const FIELD_ANSWER_PATTERNS = {
  class:    [/economy/i, /business class/i, /first class/i, /premium economy/i],
  tripType: [/one.way/i, /return/i, /round trip/i],
  visa:     [/yes.*visa/i, /have.*visa/i, /valid.*passport/i, /passport.*valid/i, /months.*valid/i, /have.*passport/i, /yes.*passport/i, /no.*visa/i],
};

export const buildFieldTracker = (allMessages = [], extracted = {}) => {
  // Sort messages chronologically
  const msgs = [...allMessages].sort((a, b) => {
    const tA = new Date(a?.received_at || a?.sent_at || a?.created_at || 0).getTime();
    const tB = new Date(b?.received_at || b?.sent_at || b?.created_at || 0).getTime();
    return tA - tB;
  });

  const outbound = msgs.filter((message) => isActualTeamReplyMessage(message));
  const inbound  = getCustomerMessages(msgs);

  // Helper: does any outbound message contain these patterns?
  const consultantAsked = (patterns) =>
    outbound.some(m => patterns.some(rx => rx.test(m?.body?.text || '')));

  // Helper: did an inbound message AFTER the last ask contain these patterns?
  const customerAnsweredAfterAsk = (askPatterns, answerPatterns) => {
    // Find index of last outbound message that asked
    let lastAskIdx = -1;
    outbound.forEach((m, i) => {
      if (askPatterns.some(rx => rx.test(m?.body?.text || ''))) lastAskIdx = i;
    });
    if (lastAskIdx === -1) return false;
    const lastAskTime = new Date(outbound[lastAskIdx]?.sent_at || outbound[lastAskIdx]?.created_at || 0).getTime();

    // Look for inbound messages after that ask time
    return inbound.some(m => {
      const t = new Date(m?.received_at || m?.created_at || 0).getTime();
      if (t <= lastAskTime) return false;
      const body = m?.body?.text || '';
      if (answerPatterns.length > 0) return answerPatterns.some(rx => rx.test(body));
      // For fields detected by value (route/date/pax), check if extracted value appears in body
      return body.trim().length > 3; // customer replied with something substantive
    });
  };

  const status = (key, detectedValue) => {
    const askPats = FIELD_ASK_PATTERNS[key] || [];
    const ansPats = FIELD_ANSWER_PATTERNS[key] || [];

    // Already have the value from customer messages → answered
    if (detectedValue) return 'answered';

    // Consultant asked AND customer replied after → answered (even if detector missed it)
    if (askPats.length && consultantAsked(askPats) && customerAnsweredAfterAsk(askPats, ansPats)) return 'answered';

    // Consultant asked but no reply yet → asked/pending
    if (askPats.length && consultantAsked(askPats)) return 'asked';

    return 'needed';
  };

  return {
    route:    status('route',    extracted.route),
    date:     status('date',     extracted.travelDate),
    pax:      status('pax',      extracted.passengerCount ? String(extracted.passengerCount) : null),
    class:    status('class',    extracted.travelClass),
    tripType: status('tripType', extracted.tripType),
    visa:     status('visa',     extracted.visaRequirement?.type === 'visa_free' ? null : extracted.visaRequirement?.label),
  };
};

// ── Segment messages into rounds (groups of inbounds between outbound replies) ──
const buildConversationSegments = (messages = []) => {
  const external = messages.filter((message) => isExternalMessage(message));

  const segments = [];
  let current = [];

  for (const msg of external) {
    if (msg.direction === "inbound") {
      current.push(msg);
    } else {
      // outbound reply — close the current inbound segment if non-empty
      if (current.length > 0) {
        segments.push({ inbounds: current, repliedAt: msg.sent_at || msg.created_at, replyId: msg.id });
        current = [];
      }
    }
  }

  // The "live" segment = remaining inbounds after the last outbound (not yet replied to)
  return { responded: segments, live: current };
};

void buildConversationSegments;

const buildConversationSegmentsWithTeamReplies = (messages = []) => {
  const external = messages.filter((message) => isExternalMessage(message));
  const segments = [];
  let current = [];

  for (const msg of external) {
    if (isActualTeamReplyMessage(msg) || hasTeamReplyEvidenceInBody(msg)) {
      if (current.length > 0) {
        segments.push({
          inbounds: current,
          repliedAt: msg.sent_at || msg.occurred_at || msg.created_at,
          replyId: msg.id,
        });
        current = [];
      }

      if (isCustomerInboundMessage(msg)) {
        current.push(sanitizeCustomerMessage(msg));
      }

      continue;
    }

    if (isCustomerInboundMessage(msg)) {
      current.push(sanitizeCustomerMessage(msg));
    }
  }

  return { responded: segments, live: current };
};

const INTENT_DEFS = {
  flight_booking:   { label: "Flight Booking",    specialist: "flights" },
  hotel_booking:    { label: "Hotel Booking",     specialist: "hotels" },
  visa_support:     { label: "Visa Support",      specialist: "visas" },
  tour_package:     { label: "Tour Package",      specialist: "tours" },
  car_hire:         { label: "Car Hire",          specialist: "transfers" },
  airport_transfer: { label: "Airport Transfer",  specialist: "transfers" },
  payment_issue:    { label: "Payment Issue",     specialist: "finance" },
  complaint:        { label: "Complaint",         specialist: "support" },
  document_request: { label: "Document Request",  specialist: "operations" },
  itinerary_request:{ label: "Itinerary Request", specialist: "operations" },
  general_inquiry:  { label: "General Inquiry",   specialist: "general" },
};

const mapAiResponseToAnalysis = (ai, customerName = "") => {
  const intentKey = ai.intent_key || "general_inquiry";
  const intentDef = INTENT_DEFS[intentKey] || INTENT_DEFS.general_inquiry;

  const sentimentMap = {
    positive: { label: "Positive", tone: "positive", color: "#22A559", icon: "😊", strong: false },
    neutral:  { label: "Neutral",  tone: "neutral",  color: "#61758C", icon: "😐", strong: false },
    negative: { label: "Negative", tone: "negative", color: "#D24B4B", icon: "😟", strong: true },
  };

  const urgencyMap = {
    high:   { label: "High",   color: "#D24B4B", bg: "#FEE2E2" },
    medium: { label: "Medium", color: "#F2A93B", bg: "#FEF3C7" },
    low:    { label: "Low",    color: "#22A559", bg: "#DCFCE7" },
  };

  const tripTypeMap = {
    one_way:    "One way",
    return:     "Return",
    multi_city: "Multi-city",
  };

  return {
    intent: {
      key:        intentKey,
      label:      ai.intent_label || intentDef.label,
      specialist: intentDef.specialist,
      confidence: ai.intent_confidence ?? 0.7,
    },
    sentiment:    sentimentMap[ai.sentiment]  || sentimentMap.neutral,
    urgency:      urgencyMap[ai.urgency]      || urgencyMap.medium,
    urgencyReason: ai.urgency_reason || null,
    extracted: {
      route:           ai.route           || null,
      travelDate:      ai.travel_date     || null,
      returnDate:      ai.return_date     || null,
      passengerCount:  ai.passenger_count || null,
      travelClass:     ai.travel_class    || null,
      tripType:        tripTypeMap[ai.trip_type] || ai.trip_type || null,
      budget:          ai.budget          || null,
      visaRequirement: null,
      tripPurpose:     null,
      multipleQuotes:  null,
    },
    nextActions:        Array.isArray(ai.next_actions) ? ai.next_actions : [],
    summary:            ai.summary || "",
    attachmentInsights: ai.attachment_insights || null,
    specialist:         intentDef.specialist,
    canAnalyze:         true,
    customerName:       customerName,
    _isAi:              true,
  };
};

export default function ConversationIntelligenceCard({
  conversation,
  messages = [],
  nextBestAction = null,
  onUseSuggestion,
  onOpenAssign,
  hideTripSignals = false,
}) {
  // Manual override state — keyed by extracted field name (e.g. "route", "travelDate")
  const [manualOverrides, setManualOverrides] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  // Dismissed segment IDs (keyed by last inbound message id in segment) — user manually archived
  const [dismissedSegmentKeys, setDismissedSegmentKeys] = useState(() => new Set());
  // Whether the archived detections panel is expanded
  const [archivedExpanded, setArchivedExpanded] = useState(false);

  // AI analysis state
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  const runAiAnalysis = useCallback(async () => {
    if (!conversation?.id) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const res = await omnichannelInboxApi.analyzeConversation(conversation.id);
      const raw = res?.data;
      if (raw && !raw.fallback) {
        const customerName = conversation?.customer?.name || conversation?.contact?.name || "";
        setAiAnalysis(mapAiResponseToAnalysis(raw, customerName));
      } else {
        setAiError(raw?.error || "Analysis unavailable");
      }
    } catch {
      setAiError("Could not reach AI analysis service");
    } finally {
      setAiLoading(false);
    }
  }, [conversation?.id]);

  useEffect(() => {
    setAiAnalysis(null);
    setAiError(null);
    runAiAnalysis();
  }, [conversation?.id]);

  const analysis = useMemo(
    () => buildConversationAnalysis(conversation, messages),
    [conversation, messages]
  );

  // Merge auto-detected values with manual overrides — single source of truth for rendering.
  // Use the full-history analysis for extracted fields (route, date, pax) so the trip
  // snapshot retains data from the entire conversation, not just the latest segment.
  const effectiveExtracted = useMemo(() => {
    const base = { ...analysis.extracted, ...(aiAnalysis?.extracted ?? {}) };
    return Object.keys(manualOverrides).length === 0 ? base : { ...base, ...manualOverrides };
  }, [analysis.extracted, aiAnalysis, manualOverrides]);

  // Track field state using effectiveExtracted so manual values register as "answered"
  const fieldTracker = useMemo(
    () => buildFieldTracker(messages, effectiveExtracted),
    [messages, effectiveExtracted]
  );

  // Segment the conversation into responded rounds + current live segment
  const { responded: respondedSegments, live: liveInbounds } = useMemo(
    () => buildConversationSegmentsWithTeamReplies(messages),
    [messages]
  );

  // Build analysis for each responded segment (for timeline/archive display)
  const respondedDetections = useMemo(
    () =>
      respondedSegments
        .map((seg) => {
          const key = seg.inbounds[seg.inbounds.length - 1]?.id;
          const ana = buildConversationAnalysis(conversation, seg.inbounds);
          return { key, analysis: ana, repliedAt: seg.repliedAt, inbounds: seg.inbounds };
        })
        .filter((d) => d.analysis.canAnalyze),
    [respondedSegments, conversation]
  );

  // Build analysis for the live (unresponded) segment only
  const heuristicLiveAnalysis = useMemo(
    () =>
      liveInbounds.length > 0
        ? buildConversationAnalysis(conversation, liveInbounds)
        : null,
    [liveInbounds, conversation]
  );

  // AI analysis overrides the heuristic when available
  const liveAnalysis = aiAnalysis ?? heuristicLiveAnalysis;

  // The "primary" analysis for the predictive next action / trip snapshot:
  // prefer AI → live segment → full-history analysis
  const primaryAnalysis = aiAnalysis ?? liveAnalysis ?? analysis;

  // Detect all service types present in the LIVE segment (not full history) to avoid stale noise
  const allServiceIntents = useMemo(() => {
    const srcMessages = liveInbounds.length > 0 ? liveInbounds : getCustomerMessages(messages);
    if (srcMessages.length === 0) return [];
    const fullText = normalize(srcMessages.map((m) => m?.body?.text || "").filter(Boolean).join(" "));
    return detectAllServiceIntents(fullText);
  }, [liveInbounds, messages]);

  if (!primaryAnalysis.canAnalyze) {
    return null;
  }

  const actionLines =
    Array.isArray(nextBestAction?.recommended_steps) && nextBestAction.recommended_steps.length
      ? nextBestAction.recommended_steps
      : primaryAnalysis.nextActions;
  const intelligenceSummary = hideTripSignals
    ? `Detected intent: ${primaryAnalysis.intent.label}. Keep the trip snapshot above as the source for route, date, passenger, and trip confirmation.`
    : primaryAnalysis.summary;

  // ── Build world-class Predictive Next Action card data ───────────────────
  const buildPredictiveAction = () => {
    const ex = effectiveExtracted;
    const intent = primaryAnalysis.intent;
    const name = primaryAnalysis.customerName.split(" ")[0] || "the customer";
    const hasVerifiedQuoteContext = Boolean(
      conversation?.quote_context?.show_quote_ui
      || conversation?.active_quote
    );

    const BOOKING_INTENTS = ["flight_booking", "hotel_booking", "tour_package", "airport_transfer", "car_hire"];
    const isBookingIntent = hasVerifiedQuoteContext && BOOKING_INTENTS.includes(intent.key);

    // STATUS LEGEND:
    //   'answered' — value detected OR consultant asked + customer replied → ✓ green
    //   'asked'    — consultant asked but no reply yet                    → ⏳ amber (waiting)
    //   'needed'   — not yet raised at all                                → ✗ red (ask now)

    // Intent-aware field definitions — labels adapt to booking type
    let quoteFields;
    if (intent.key === "hotel_booking") {
      quoteFields = [
        { key: "route",    label: "Destination", value: ex.route,         status: fieldTracker.route,    icon: "🏨" },
        { key: "date",     label: "Check-in",    value: ex.travelDate,    status: fieldTracker.date,     icon: "📅" },
        { key: "pax",      label: "Guests",      value: ex.passengerCount ? `${ex.passengerCount}` : null, status: fieldTracker.pax, icon: "👤" },
        { key: "class",    label: "Room type",   value: ex.travelClass,   status: fieldTracker.class,    icon: "🛏" },
      ];
    } else if (intent.key === "tour_package") {
      quoteFields = [
        { key: "route",    label: "Destination", value: ex.route,         status: fieldTracker.route,    icon: "🗺" },
        { key: "date",     label: "Tour date",   value: ex.travelDate,    status: fieldTracker.date,     icon: "📅" },
        { key: "pax",      label: "Travellers",  value: ex.passengerCount ? `${ex.passengerCount}` : null, status: fieldTracker.pax, icon: "👤" },
        { key: "tripType", label: "Duration",    value: ex.tripType,      status: fieldTracker.tripType, icon: "⏱" },
      ];
    } else if (intent.key === "car_hire") {
      quoteFields = [
        { key: "route",    label: "Route",       value: ex.route,         status: fieldTracker.route,    icon: "🚗" },
        { key: "date",     label: "Pick-up date",value: ex.travelDate,    status: fieldTracker.date,     icon: "📅" },
        { key: "pax",      label: "Passengers",  value: ex.passengerCount ? `${ex.passengerCount}` : null, status: fieldTracker.pax, icon: "👤" },
        { key: "tripType", label: "Duration",    value: ex.tripType,      status: fieldTracker.tripType, icon: "⏱" },
      ];
    } else if (intent.key === "airport_transfer") {
      quoteFields = [
        { key: "route",    label: "Route",          value: ex.route,      status: fieldTracker.route,    icon: "🚗" },
        { key: "date",     label: "Transfer date",  value: ex.travelDate, status: fieldTracker.date,     icon: "📅" },
        { key: "pax",      label: "Passengers",     value: ex.passengerCount ? `${ex.passengerCount}` : null, status: fieldTracker.pax, icon: "👤" },
      ];
    } else {
      // flight_booking
      quoteFields = [
        { key: "route",    label: "Route",     value: ex.route,         status: fieldTracker.route,    icon: "✈" },
        { key: "date",     label: "Date",      value: ex.travelDate,    status: fieldTracker.date,     icon: "📅" },
        { key: "pax",      label: "Pax",       value: ex.passengerCount ? `${ex.passengerCount}` : null, status: fieldTracker.pax, icon: "👤" },
        { key: "class",    label: "Class",     value: ex.travelClass,   status: fieldTracker.class,    icon: "💺" },
        { key: "tripType", label: "Trip type", value: ex.tripType,      status: fieldTracker.tripType, icon: "🔄" },
      ];
    }

    const answeredFields = quoteFields.filter(f => f.status === 'answered');
    const askedFields    = quoteFields.filter(f => f.status === 'asked');
    const neededFields   = quoteFields.filter(f => f.status === 'needed');
    const allDone        = neededFields.length === 0 && askedFields.length === 0;

    // Smart message only asks for NEEDED fields (not already asked or answered)
    const buildSmartMessage = () => {
      if (neededFields.length === 0) return null;

      const greeting = `Hi ${name}! 😊`;
      const parts = [];
      const needed = neededFields.map(f => f.key);
      let num = 1;

      if (intent.key === "hotel_booking") {
        parts.push("To find you the best hotel options, could you please confirm:");
        if (needed.includes("route"))    parts.push(`${num++}. Your destination / city`);
        if (needed.includes("date"))     parts.push(`${num++}. Check-in date (and check-out date if known)`);
        if (needed.includes("pax"))      parts.push(`${num++}. Number of guests`);
        if (needed.includes("class"))    parts.push(`${num++}. Preferred room type (standard, deluxe, suite, etc.)`);
      } else if (intent.key === "tour_package") {
        parts.push("To find the best tour package for you, could you please share:");
        if (needed.includes("route"))    parts.push(`${num++}. Your desired destination`);
        if (needed.includes("date"))     parts.push(`${num++}. Preferred tour dates`);
        if (needed.includes("pax"))      parts.push(`${num++}. Number of travellers`);
        if (needed.includes("tripType")) parts.push(`${num++}. How many days / nights are you looking at?`);
      } else if (intent.key === "car_hire") {
        parts.push("To find the best vehicle for you, could you please confirm:");
        if (needed.includes("route"))    parts.push(`${num++}. Pick-up and drop-off location`);
        if (needed.includes("date"))     parts.push(`${num++}. Pick-up date and time (and return date if applicable)`);
        if (needed.includes("pax"))      parts.push(`${num++}. Number of passengers`);
        if (needed.includes("tripType")) parts.push(`${num++}. How many days do you need the vehicle?`);
      } else if (intent.key === "airport_transfer") {
        parts.push("To arrange your transfer, could you please confirm:");
        if (needed.includes("route"))    parts.push(`${num++}. Pickup location and drop-off destination`);
        if (needed.includes("date"))     parts.push(`${num++}. Transfer date and time`);
        if (needed.includes("pax"))      parts.push(`${num++}. Number of passengers`);
      } else {
        // flight_booking
        parts.push("To prepare your quote accurately, could you please confirm:");
        if (needed.includes("route"))    parts.push(`${num++}. Your departure city and destination`);
        if (needed.includes("date"))     parts.push(`${num++}. Your preferred travel date${ex.tripType === "Return" ? " (and return date)" : " (and return date if applicable)"}`);
        if (needed.includes("pax"))      parts.push(`${num++}. Number of passengers travelling`);
        if (needed.includes("class"))    parts.push(`${num++}. Preferred cabin class (economy, business, or first)`);
        if (needed.includes("tripType")) parts.push(`${num++}. One-way or return trip?`);
      }

      if (parts.length === 1) return null;
      return `${greeting}\n${parts.join("\n")}`;
    };

    // Build waiting message for fields already asked but pending customer reply
    const buildWaitingNote = () => {
      if (askedFields.length === 0) return null;
      const labels = askedFields.map(f => f.label.toLowerCase());
      return `Waiting for customer to confirm: ${labels.join(", ")} — no reply yet.`;
    };

    // "All done" CTA message — intent-aware
    const buildCtaMessage = () => {
      if (intent.key === "hotel_booking") {
        return `Great news! I'm now searching for the best hotel options in ${ex.route || "your destination"} for ${ex.passengerCount || "your group"} guest${ex.passengerCount !== 1 ? "s" : ""} checking in on ${ex.travelDate || "your selected date"}. I'll send the options shortly!`;
      }
      if (intent.key === "tour_package") {
        return `I'm putting together the best tour packages to ${ex.route || "your destination"} for ${ex.passengerCount || "your group"} traveller${ex.passengerCount !== 1 ? "s" : ""} on ${ex.travelDate || "your preferred dates"}. I'll share options shortly!`;
      }
      if (intent.key === "airport_transfer") {
        return `I'm arranging your airport transfer for ${ex.travelDate || "your travel date"}. I'll send the confirmation details shortly!`;
      }
      return `I'm preparing your flight quote for ${ex.route || "your route"} on ${ex.travelDate || "your travel date"} for ${ex.passengerCount || "the"} passenger${ex.passengerCount !== 1 ? "s" : ""}. I'll send it shortly!`;
    };

    const THEMES = {
      ready:   { bar: "linear-gradient(90deg,#16a34a,#22c55e)", icon: "✅", title: "Ready — Prepare & send the quote",   urgencyLabel: "ACTION READY" },
      waiting: { bar: "linear-gradient(90deg,#0369a1,#38bdf8)", icon: "⏳", title: null,                                 urgencyLabel: "WAITING FOR REPLY" },
      collect: { bar: "linear-gradient(90deg,#d97706,#f59e0b)", icon: "📋", title: null,                                 urgencyLabel: "DETAILS NEEDED" },
      negative:{ bar: "linear-gradient(90deg,#dc2626,#ef4444)", icon: "🚨", title: "De-escalate now",                   urgencyLabel: "URGENT" },
      ticket:  { bar: "linear-gradient(90deg,#7c3aed,#8b5cf6)", icon: "🎫", title: `Handle ${intent.label}`,            urgencyLabel: "IN PROGRESS" },
      general: { bar: "linear-gradient(90deg,#0369a1,#3b82f6)", icon: "💬", title: `${intent.label}`,                   urgencyLabel: "MONITORING" },
    };

    let theme, smartMessage = null, waitingNote = null;

    if (intent.key === "complaint" || primaryAnalysis.sentiment.label === "Negative") {
      theme = THEMES.negative;
    } else if (intent.key === "ticket_change" || intent.key === "refund_request") {
      theme = THEMES.ticket;
    } else if (isBookingIntent) {
      if (allDone) {
        theme = THEMES.ready;
      } else if (neededFields.length === 0 && askedFields.length > 0) {
        // All un-answered fields have been asked — just waiting
        const askedLabels = askedFields.map(f => f.label.toLowerCase());
        theme = { ...THEMES.waiting, title: `Waiting on ${askedLabels.join(", ")} from ${name}` };
        waitingNote = buildWaitingNote();
      } else {
        // Still have fields to ask
        const needLabels = neededFields.slice(0, 2).map(f => f.label.toLowerCase());
        const extra = neededFields.length > 2 ? ` + ${neededFields.length - 2} more` : "";
        theme = { ...THEMES.collect, title: `Ask ${name}: ${needLabels.join(", ")}${extra}` };
        smartMessage = buildSmartMessage();
        waitingNote = buildWaitingNote();
      }
    } else {
      theme = THEMES.general;
    }

    // Only use server label when it's a meaningful specific label, not a generic catch-all
    const GENERIC_SERVER_LABELS = ["collect missing quote details", "monitor conversation", "continue monitoring", "monitoring"];
    if (nextBestAction?.label && !GENERIC_SERVER_LABELS.includes(nextBestAction.label.toLowerCase().trim())) {
      theme = { ...theme, title: nextBestAction.label };
    }

    return { theme, quoteFields, answeredFields, askedFields, neededFields, allDone, smartMessage, waitingNote, intent, ex, isBookingIntent, ctaMessage: buildCtaMessage() };
  };

  const pred = buildPredictiveAction();

  const SL = { fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em", color: "#bbbbbb", marginBottom: 6, fontFamily: "'Inter',system-ui,sans-serif" };
  const CARD = { background: "#fff", border: "1px solid #efefef", borderRadius: 10, padding: 12 };
  const PAD = { padding: "0 16px" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "0 0 14px", fontFamily: "'Inter',system-ui,sans-serif" }}>

      {/* ══ Predictive Next Action — World-class redesign ══ */}
      <div style={PAD}>
        <div style={SL}>Predictive next action</div>
        <div style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>

          {/* ── Gradient header bar ── */}
          <div style={{ background: pred.theme.bar, padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 18, lineHeight: 1 }}>{pred.theme.icon}</span>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.75)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>
                  {pred.theme.urgencyLabel}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#ffffff", lineHeight: 1.25, maxWidth: 200 }}>
                  {pred.theme.title}
                </div>
              </div>
            </div>
            {/* Progress fraction for booking intents */}
            {pred.isBookingIntent && pred.quoteFields.length > 0 && (
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#ffffff", lineHeight: 1 }}>
                  {pred.answeredFields.length}/{pred.quoteFields.length}
                </div>
                <div style={{ fontSize: 9, color: "rgba(255,255,255,0.7)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  fields
                </div>
              </div>
            )}
          </div>

          {/* ── Field status chips — three states ── */}
          {pred.isBookingIntent && (
            <div style={{ padding: "10px 14px 8px", background: "#ffffff", borderBottom: "1px solid #f3f4f6" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Quote fields
                </div>
                <div style={{ fontSize: 10, color: "#9ca3af" }}>
                  <span style={{ color: "#16a34a", fontWeight: 700 }}>{pred.answeredFields.length}</span> done
                  {pred.askedFields.length > 0 && <> · <span style={{ color: "#d97706", fontWeight: 700 }}>{pred.askedFields.length}</span> waiting</>}
                  {pred.neededFields.length > 0 && <> · <span style={{ color: "#dc2626", fontWeight: 700 }}>{pred.neededFields.length}</span> needed</>}
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {pred.quoteFields.map(f => {
                  const styles = {
                    answered: { bg: "#dcfce7", color: "#166534", border: "#bbf7d0", mark: "✓" },
                    asked:    { bg: "#fef3c7", color: "#92400e", border: "#fde68a", mark: "⏳" },
                    needed:   { bg: "#fef2f2", color: "#dc2626", border: "#fecaca", mark: "✗" },
                  };
                  const s = styles[f.status] || styles.needed;
                  const displayValue = f.status === 'answered' && f.value ? `: ${f.value}` : '';
                  const tooltip = f.status === 'asked' ? 'Asked — waiting for reply' : '';
                  return (
                    <div
                      key={f.key}
                      title={tooltip}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        padding: "3px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                        background: s.bg, color: s.color, border: `1px solid ${s.border}`,
                        cursor: tooltip ? "help" : "default",
                      }}
                    >
                      <span style={{ fontSize: 10 }}>{s.mark}</span>
                      <span>{f.label}{displayValue}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Smart composed message ── */}
          {pred.smartMessage && (
            <div style={{ padding: "10px 14px", background: "#fffbeb", borderBottom: "1px solid #fef3c7" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6, display: "flex", justifyContent: "space-between" }}>
                <span>Smart reply — ready to send</span>
                <span style={{ fontWeight: 400, color: "#a16207", textTransform: "none", letterSpacing: 0 }}>click to use ↓</span>
              </div>
              <div
                onClick={() => onUseSuggestion?.(pred.smartMessage)}
                onMouseEnter={e => e.currentTarget.style.background = "#fef9c3"}
                onMouseLeave={e => e.currentTarget.style.background = "#ffffff"}
                style={{
                  background: "#ffffff",
                  border: "1.5px solid #fde68a",
                  borderRadius: 8,
                  padding: "10px 12px",
                  cursor: "pointer",
                  transition: "background 120ms",
                }}
              >
                <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.55, whiteSpace: "pre-wrap", fontFamily: "inherit" }}>
                  {pred.smartMessage}
                </div>
                <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
                  <div style={{
                    background: "#d97706", color: "#ffffff",
                    fontSize: 11, fontWeight: 700,
                    padding: "5px 14px", borderRadius: 7,
                    letterSpacing: "0.02em",
                  }}>
                    Send this now →
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Waiting note: fields asked but no reply yet ── */}
          {pred.waitingNote && !pred.smartMessage && (
            <div style={{ padding: "8px 14px", background: "#f0f9ff", borderBottom: "1px solid #bae6fd" }}>
              <div style={{ fontSize: 11, color: "#0369a1", lineHeight: 1.5, display: "flex", gap: 6, alignItems: "flex-start" }}>
                <span style={{ flexShrink: 0 }}>⏳</span>
                <span>{pred.waitingNote}</span>
              </div>
            </div>
          )}

          {/* ── "Ready to quote" CTA when all fields collected ── */}
          {pred.isBookingIntent && pred.allDone && (
            <div style={{ padding: "12px 14px", background: "#f0fdf4" }}>
              <div style={{ fontSize: 12, color: "#166534", fontWeight: 600, marginBottom: 4 }}>
                All required details collected ✓
              </div>
              <div style={{ fontSize: 11, color: "#4b7a5a", lineHeight: 1.45, marginBottom: 8 }}>
                {pred.answeredFields.map(f => `${f.label}: ${f.value || "—"}`).join(" · ")}
              </div>
              <div
                onClick={() => onUseSuggestion?.(pred.ctaMessage)}
                onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
                onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                style={{
                  background: "#16a34a", color: "#ffffff",
                  fontSize: 12, fontWeight: 700,
                  padding: "7px 16px", borderRadius: 8,
                  cursor: "pointer", display: "inline-block",
                  transition: "opacity 120ms",
                }}
              >
                ✓ Send acknowledgement →
              </div>
            </div>
          )}

          {/* ── Complaint / escalation CTA ── */}
          {(pred.intent.key === "complaint" || primaryAnalysis.sentiment.label === "Negative") && (
            <div style={{ padding: "10px 14px", background: "#fff1f2" }}>
              <div style={{ fontSize: 11, color: "#881337", lineHeight: 1.5, marginBottom: 8 }}>
                {nextBestAction?.reason || "Acknowledge frustration immediately — delays make negative sentiment worse."}
              </div>
              <div
                onClick={() => onUseSuggestion?.("I sincerely apologize for the inconvenience you've experienced. Your concern is important to us and I want to make sure we resolve this as quickly as possible. Could you please share more details so I can assist you right away?")}
                onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
                onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                style={{
                  background: "#dc2626", color: "#ffffff",
                  fontSize: 11, fontWeight: 700,
                  padding: "6px 14px", borderRadius: 7,
                  cursor: "pointer", display: "inline-block",
                }}
              >
                🚨 Send apology now →
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ══ Live Intelligence — multi-detection, segmented by round ══ */}
      <div style={PAD}>
        <div style={{ ...SL, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <span>Live intelligence</span>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            {aiLoading && (
              <span style={{ fontSize: 9, color: "#9aa9a0", fontStyle: "italic" }}>Analysing…</span>
            )}
            {aiAnalysis && !aiLoading && (
              <span style={{
                fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 10,
                background: "#eff6ff", color: "#1d4ed8", letterSpacing: "0.05em",
              }}>
                ✨ AI
              </span>
            )}
            {aiError && !aiLoading && (
              <span
                title={aiError}
                style={{
                  fontSize: 9, fontWeight: 600, padding: "2px 6px", borderRadius: 10,
                  background: "#fff7ed", color: "#c2410c", cursor: "pointer",
                }}
                onClick={runAiAnalysis}
              >
                ⚠ Retry AI
              </span>
            )}
            {!aiLoading && (
              <button
                onClick={runAiAnalysis}
                title="Re-analyse with AI"
                style={{
                  fontSize: 9, fontWeight: 600, padding: "2px 7px", borderRadius: 10,
                  background: "transparent", border: "1px solid #d1fae5", color: "#166534",
                  cursor: "pointer", lineHeight: 1.5,
                }}
              >
                ↻ Re-analyse
              </button>
            )}
            {liveInbounds.length > 0 && (
              <span style={{
                fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 10,
                background: "#dcfce7", color: "#166534", letterSpacing: "0.06em", textTransform: "uppercase",
              }}>
                ● LIVE
              </span>
            )}
          </div>
        </div>

        {/* ── AI attachment insights ── */}
        {aiAnalysis?.attachmentInsights && (
          <div style={{
            background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8,
            padding: "7px 11px", display: "flex", gap: 7, alignItems: "flex-start", marginBottom: 6,
          }}>
            <span style={{ fontSize: 13, flexShrink: 0 }}>📎</span>
            <div style={{ fontSize: 11, color: "#1e40af", lineHeight: 1.5 }}>
              <strong>Attachment insight:</strong> {aiAnalysis.attachmentInsights}
            </div>
          </div>
        )}

        {/* ── Suppression notice — muted detection ── */}
        {liveInbounds.length > 0 && liveAnalysis?.suppression?.suppressed && (
          <div style={{
            background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8,
            padding: "8px 12px", display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6,
          }}>
            <span style={{ fontSize: 14, flexShrink: 0 }}>🔕</span>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", marginBottom: 2 }}>
                Quote detection muted
              </div>
              <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.45 }}>
                Existing booking or dialogue signals detected — treating this as an active conversation, not a new inquiry.
              </div>
            </div>
          </div>
        )}

        {/* ── Soft suppression — low-confidence advisory ── */}
        {liveInbounds.length > 0 && liveAnalysis?.suppression?.softSuppressed && !liveAnalysis?.suppression?.suppressed && (
          <div style={{
            background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8,
            padding: "6px 10px", display: "flex", gap: 7, alignItems: "center", marginBottom: 6,
          }}>
            <span style={{ fontSize: 12 }}>⚠</span>
            <div style={{ fontSize: 11, color: "#92400e" }}>
              Possible existing booking context — verify this is a new inquiry before promoting.
            </div>
          </div>
        )}

        {/* ── Current live detection (unresponded segment) ── */}
        {liveInbounds.length > 0 && liveAnalysis?.canAnalyze ? (
          <div style={{
            ...CARD, padding: 0, overflow: "hidden",
            border: "1.5px solid #bbf7d0",
            boxShadow: "0 2px 12px -4px rgba(22,163,74,0.12)",
          }}>
            {/* Live header */}
            <div style={{ background: "linear-gradient(90deg,#f0fdf4,#dcfce7)", padding: "8px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, borderBottom: "1px solid #bbf7d0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <span style={{ fontSize: 14 }}>🔍</span>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#166534" }}>
                      {liveAnalysis.intent.key === "general_inquiry" ? "General Inquiry" : `New detection · ${liveAnalysis.intent.label}`}
                    </div>
                    {liveAnalysis.intent.confidence !== undefined && liveAnalysis.intent.key !== "general_inquiry" && (
                      <div style={{
                        fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 6,
                        background: liveAnalysis.intent.confidence >= 0.75 ? "#dcfce7" : liveAnalysis.intent.confidence >= 0.4 ? "#fef3c7" : "#fee2e2",
                        color: liveAnalysis.intent.confidence >= 0.75 ? "#166534" : liveAnalysis.intent.confidence >= 0.4 ? "#92400e" : "#b91c1c",
                        letterSpacing: "0.04em", textTransform: "uppercase",
                      }}>
                        {liveAnalysis.intent.confidence >= 0.75 ? "HIGH" : liveAnalysis.intent.confidence >= 0.4 ? "MEDIUM" : "LOW"} CONFIDENCE
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 10, color: "#4b7a5a" }}>
                    {liveInbounds.length} unresponded message{liveInbounds.length !== 1 ? "s" : ""} · {liveAnalysis.urgency.label} urgency · {liveAnalysis.sentiment.label}
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  const key = liveInbounds[liveInbounds.length - 1]?.id;
                  if (key) setDismissedSegmentKeys((prev) => new Set([...prev, key]));
                }}
                style={{
                  background: "none", border: "1px solid #86efac", borderRadius: 6,
                  fontSize: 10, fontWeight: 600, color: "#166534",
                  padding: "3px 8px", cursor: "pointer", flexShrink: 0,
                }}
                title="Archive this detection — it will move to detection history"
              >
                Archive ✕
              </button>
            </div>

            {/* Detection chips */}
            <div style={{ padding: "8px 12px 6px", display: "flex", flexWrap: "wrap", gap: 5, borderBottom: "1px solid #f0f4f1" }}>
              {[
                { label: `Agenda: ${liveAnalysis.intent.label}`, icon: "📋" },
                { label: `Sentiment: ${liveAnalysis.sentiment.label}`, icon: liveAnalysis.sentiment.label === "Negative" ? "😟" : liveAnalysis.sentiment.label === "Positive" ? "😊" : "😐" },
                { label: `Urgency: ${liveAnalysis.urgency.label}`, icon: "⚡" },
                { label: `Desk: ${liveAnalysis.specialist}`, icon: "🏢" },
              ].map((chip) => (
                <span key={chip.label} style={{ fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 20, border: "1px solid #e5e7eb", color: "#374151", display: "flex", alignItems: "center", gap: 3, whiteSpace: "nowrap", background: "#fff" }}>
                  <span style={{ fontSize: 10 }}>{chip.icon}</span>{chip.label}
                </span>
              ))}
            </div>

            {/* Summary + quick actions */}
            <div style={{ padding: "8px 12px 10px" }}>
              <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.55, marginBottom: liveAnalysis.nextActions.length > 0 ? 8 : 0 }}>
                {liveAnalysis.summary}
              </div>
              {liveAnalysis.nextActions.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {liveAnalysis.nextActions.slice(0, 3).map((action, i) => (
                    <div key={i} style={{ display: "flex", gap: 7, alignItems: "flex-start" }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: "#16a34a", flexShrink: 0, marginTop: 1 }}>{i + 1}.</span>
                      <span style={{ fontSize: 11, color: "#4b5563", lineHeight: 1.45 }}>{action}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ ...CARD, padding: "10px 12px" }}>
            {/* Full-history (no live segment) — classic mode */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#111", marginBottom: 1 }}>{primaryAnalysis.intent.label}</div>
                <div style={{ fontSize: 11, color: "#aaa", lineHeight: 1.4 }}>
                  {primaryAnalysis.urgency.label} urgency · {primaryAnalysis.sentiment.label} sentiment
                </div>
              </div>
              <span style={{ fontSize: 10, fontWeight: 600, color: "#888", padding: "2px 8px", border: "1px solid #e5e7eb", borderRadius: 10, flexShrink: 0 }}>
                {primaryAnalysis.specialist}
              </span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 7 }}>
              {[
                `Agenda: ${primaryAnalysis.intent.label}`,
                `Sentiment: ${primaryAnalysis.sentiment.label}`,
                `Urgency: ${primaryAnalysis.urgency.label}`,
                `Desk: ${primaryAnalysis.specialist}`,
              ].map((chip) => (
                <span key={chip} style={{ fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 20, border: "1px solid #e5e5e5", color: "#444", whiteSpace: "nowrap" }}>
                  {chip}
                </span>
              ))}
            </div>
            <div style={{ fontSize: 12, color: "#555", lineHeight: 1.5 }}>{intelligenceSummary}</div>
          </div>
        )}

        {/* ── Detection history — responded segments ── */}
        {respondedDetections.filter((d) => !dismissedSegmentKeys.has(d.key)).length > 0 && (
          <div style={{ marginTop: 8 }}>
            <button
              onClick={() => setArchivedExpanded((prev) => !prev)}
              style={{
                background: "none", border: "none", cursor: "pointer", padding: 0,
                display: "flex", alignItems: "center", gap: 5, width: "100%",
              }}
            >
              <div style={{ flex: 1, height: 1, background: "#f0f0f0" }} />
              <span style={{ fontSize: 10, fontWeight: 600, color: "#bbb", whiteSpace: "nowrap", letterSpacing: "0.05em" }}>
                {archivedExpanded ? "▲" : "▼"} {respondedDetections.filter((d) => !dismissedSegmentKeys.has(d.key)).length} detection{respondedDetections.filter((d) => !dismissedSegmentKeys.has(d.key)).length !== 1 ? "s" : ""} in history
              </span>
              <div style={{ flex: 1, height: 1, background: "#f0f0f0" }} />
            </button>

            {archivedExpanded && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
                {respondedDetections
                  .filter((d) => !dismissedSegmentKeys.has(d.key))
                  .slice()
                  .reverse() // newest first
                  .map((det) => (
                    <div key={det.key} style={{
                      background: "#fafafa", border: "1px solid #e5e7eb", borderRadius: 8,
                      padding: "8px 10px", display: "flex", gap: 10, alignItems: "flex-start",
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 3 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#374151" }}>
                            {det.analysis.intent.label}
                          </span>
                          <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 8, background: "#f3f4f6", color: "#6b7280", border: "1px solid #e5e7eb" }}>
                            {det.analysis.sentiment.label}
                          </span>
                          <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 8, background: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0" }}>
                            Handled ✓
                          </span>
                        </div>
                        <div style={{ fontSize: 11, color: "#6b7280", lineHeight: 1.45 }}>
                          {det.analysis.summary}
                        </div>
                        {det.analysis.extracted.route && (
                          <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 3 }}>
                            Route: {det.analysis.extracted.route}
                            {det.analysis.extracted.travelDate ? ` · ${det.analysis.extracted.travelDate}` : ""}
                            {det.analysis.extracted.passengerCount ? ` · ${det.analysis.extracted.passengerCount} pax` : ""}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => setDismissedSegmentKeys((prev) => new Set([...prev, det.key]))}
                        style={{ background: "none", border: "none", color: "#ccc", cursor: "pointer", fontSize: 13, padding: "0 0 0 4px", flexShrink: 0, lineHeight: 1 }}
                        title="Remove from history"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Trip Snapshot ── */}
      {!hideTripSignals && (
        <div style={PAD}>
          <div style={{ ...SL, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Trip snapshot</span>
            <span style={{ fontWeight: 400, color: "#bbbbbb", textTransform: "none", letterSpacing: 0, fontSize: 10 }}>click ✏ to edit any field</span>
          </div>
          <div style={CARD}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {[
                { fieldKey: "passengerCount", label: "PASSENGERS",    value: effectiveExtracted.passengerCount ? String(effectiveExtracted.passengerCount) : null, needed: true },
                { fieldKey: "tripType",       label: "TRIP TYPE",     value: effectiveExtracted.tripType || null, needed: false },
                { fieldKey: "route",          label: "ROUTE",         value: effectiveExtracted.route || null, needed: true },
                { fieldKey: "travelDate",     label: "TRAVEL DATE",   value: effectiveExtracted.travelDate || null, needed: true },
                { fieldKey: "returnDate",     label: "RETURN DATE",   value: effectiveExtracted.returnDate || null, needed: false },
                { fieldKey: "quotationCount", label: "QUOTES NEEDED", value: effectiveExtracted.quotationCount ? String(effectiveExtracted.quotationCount) : null, needed: false },
              ].map(({ fieldKey, label, value, needed }) => {
                const isEditing = editingField === fieldKey;
                const isOverridden = manualOverrides[fieldKey] !== undefined;
                return (
                  <div key={fieldKey} style={{ background: "#f9f9f9", borderRadius: 8, padding: "9px 11px", minWidth: 0, overflow: "hidden" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                      <div style={{ fontSize: 10, color: "#aaaaaa", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>{label}</div>
                      <button
                        onClick={() => {
                          if (isEditing) { setEditingField(null); }
                          else { setEditingField(fieldKey); setEditValue(value || ""); }
                        }}
                        style={{ background: "none", border: "none", cursor: "pointer", color: isOverridden ? "#2563eb" : "#cccccc", fontSize: 11, padding: "0 0 0 4px", lineHeight: 1, flexShrink: 0 }}
                        title={isOverridden ? "Manually set — click to edit" : "Edit manually"}
                      >✏</button>
                    </div>
                    {isEditing ? (
                      <div style={{ display: "flex", gap: 4, marginTop: 3 }}>
                        <input
                          autoFocus
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              const trimmed = editValue.trim();
                              setManualOverrides((prev) => {
                                if (!trimmed) { const next = { ...prev }; delete next[fieldKey]; return next; }
                                return { ...prev, [fieldKey]: trimmed };
                              });
                              setEditingField(null);
                            }
                            if (e.key === "Escape") setEditingField(null);
                          }}
                          placeholder={`Enter ${label.toLowerCase()}…`}
                          style={{ flex: 1, fontSize: 11, border: "1px solid #bbf7d0", borderRadius: 4, padding: "3px 7px", outline: "none", minWidth: 0, fontFamily: "inherit" }}
                        />
                        <button
                          onClick={() => {
                            const trimmed = editValue.trim();
                            setManualOverrides((prev) => {
                              if (!trimmed) { const next = { ...prev }; delete next[fieldKey]; return next; }
                              return { ...prev, [fieldKey]: trimmed };
                            });
                            setEditingField(null);
                          }}
                          style={{ background: "#16a34a", color: "#fff", border: "none", borderRadius: 4, padding: "3px 8px", cursor: "pointer", fontSize: 11, flexShrink: 0 }}
                        >✓</button>
                      </div>
                    ) : (
                      <>
                        <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: value ? (isOverridden ? "#2563eb" : "#d97706") : "#bbbbbb", fontStyle: value ? "normal" : "italic" }}>{value || "Not detected"}</div>
                        <div style={{ fontSize: 10, fontWeight: 500, marginTop: 2, whiteSpace: "nowrap", color: value ? (isOverridden ? "#2563eb" : "#d97706") : (needed ? "#c2410c" : "#bbbbbb") }}>
                          {value ? (isOverridden ? "✏ Manual" : "✓ Detected") : (needed ? "! Needed" : "—")}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Visa Intelligence Banner ── */}
      {!hideTripSignals && effectiveExtracted.visaRequirement && effectiveExtracted.visaRequirement.type !== "visa_free" && (() => {
        const visa = effectiveExtracted.visaRequirement;
        const styles = {
          visa_required:   { bg: "#fffbeb", border: "#fde68a", icon: "⚠", iconColor: "#d97706", text: "#92400e" },
          e_visa:          { bg: "#eff6ff", border: "#bfdbfe", icon: "ℹ", iconColor: "#1d4ed8", text: "#1e3a5f" },
          eta_required:    { bg: "#eff6ff", border: "#bfdbfe", icon: "ℹ", iconColor: "#1d4ed8", text: "#1e3a5f" },
          visa_on_arrival: { bg: "#f0fdf4", border: "#bbf7d0", icon: "✓", iconColor: "#16a34a", text: "#14532d" },
        };
        const s = styles[visa.type] || styles.visa_required;
        return (
          <div style={{ margin: "0 16px", background: s.bg, border: `1px solid ${s.border}`, borderRadius: 8, padding: "10px 12px", display: "flex", gap: 8, alignItems: "flex-start" }}>
            <span style={{ color: s.iconColor, fontSize: 15, flexShrink: 0, lineHeight: 1 }}>{s.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: s.text, marginBottom: 2 }}>{visa.label}</div>
              {visa.action && <div style={{ fontSize: 11, color: s.text, lineHeight: 1.5, opacity: 0.9 }}>{visa.action}</div>}
            </div>
          </div>
        );
      })()}

      {/* ── Multiple Service Types Detected ── */}
      {!hideTripSignals && allServiceIntents.length >= 2 && (
        <div style={PAD}>
          <div style={SL}>Multiple requests detected</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {allServiceIntents.map((svc) => {
              const icons = {
                flight_booking: "✈", hotel_booking: "🏨", car_hire: "🚗",
                tour_package: "🗺", visa_support: "📄", airport_transfer: "🚖",
                ticket_change: "🎫", refund_request: "💰",
              };
              return (
                <div key={svc.key} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 12px", display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0 }}>
                    {icons[svc.key] || "💬"}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#111" }}>{svc.label}</div>
                    <div style={{ fontSize: 10, color: "#888" }}>{svc.specialist} desk</div>
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 600, background: "#f0fdf4", color: "#166634", padding: "2px 8px", borderRadius: 10, border: "1px solid #bbf7d0", flexShrink: 0 }}>
                    Detected
                  </div>
                </div>
              );
            })}
            <div style={{ fontSize: 11, color: "#888", lineHeight: 1.45 }}>
              This customer has multiple requests. Confirm priorities and handle each separately or route to the relevant desk.
            </div>
          </div>
        </div>
      )}

      {/* ── Quotation Breakdown (multi-quote emails) ── */}
      {!hideTripSignals && effectiveExtracted.multipleQuotes?.length >= 2 && (
        <div style={PAD}>
          <div style={SL}>Quotation breakdown</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {effectiveExtracted.multipleQuotes.map((q) => {
              const visaStyles = {
                visa_required:   { bg: "#fffbeb", border: "#fde68a", icon: "⚠", color: "#92400e" },
                e_visa:          { bg: "#eff6ff", border: "#bfdbfe", icon: "ℹ", color: "#1e3a5f" },
                eta_required:    { bg: "#eff6ff", border: "#bfdbfe", icon: "ℹ", color: "#1e3a5f" },
                visa_on_arrival: { bg: "#f0fdf4", border: "#bbf7d0", icon: "✓", color: "#14532d" },
                visa_free:       { bg: "#f0fdf4", border: "#bbf7d0", icon: "✓", color: "#14532d" },
              };
              const vs = q.visa ? (visaStyles[q.visa.type] || visaStyles.visa_required) : null;
              return (
                <div key={q.number} style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 10, padding: 12 }}>
                  {/* Header row */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#111" }}>Quotation {q.number}</span>
                    {q.passengerCount && (
                      <span style={{ fontSize: 11, fontWeight: 600, background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#166534", padding: "2px 8px", borderRadius: 10 }}>
                        {q.passengerCount} pax
                      </span>
                    )}
                  </div>

                  {/* Route / dates row */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: q.travellers?.length > 0 ? 8 : 0 }}>
                    {[
                      { label: "ROUTE",      value: q.routeLabel },
                      { label: "DESTINATION",value: q.destination },
                      { label: "DEPART",     value: q.departureDate },
                      { label: "RETURN",     value: q.returnDate },
                    ].map(({ label, value }) => (
                      <div key={label} style={{ background: "#f9f9f9", borderRadius: 6, padding: "6px 9px" }}>
                        <div style={{ fontSize: 9, color: "#aaa", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 2 }}>{label}</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: value ? "#d97706" : "#bbbbbb", fontStyle: value ? "normal" : "italic", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value || "—"}</div>
                      </div>
                    ))}
                  </div>

                  {/* Traveller names */}
                  {q.travellers?.length > 0 && (
                    <div style={{ marginTop: 6 }}>
                      <div style={{ fontSize: 9, color: "#aaa", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>Travellers</div>
                      {q.travellers.map((t, idx) => (
                        <div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "3px 0", borderTop: idx > 0 ? "1px solid #f0f0f0" : "none" }}>
                          <span style={{ fontSize: 11, color: "#333", fontWeight: 500 }}>{t.name}</span>
                          <span style={{ fontSize: 10, fontWeight: 600, color: t.travelClass?.toLowerCase().includes("business") ? "#1d4ed8" : "#555", background: t.travelClass?.toLowerCase().includes("business") ? "#eff6ff" : "#f5f5f5", padding: "1px 7px", borderRadius: 8, whiteSpace: "nowrap", flexShrink: 0, marginLeft: 6 }}>{t.travelClass}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Visa badge for this destination */}
                  {q.visa && q.visa.type !== "visa_free" && vs && (
                    <div style={{ marginTop: 8, background: vs.bg, border: `1px solid ${vs.border}`, borderRadius: 6, padding: "6px 10px", display: "flex", gap: 6, alignItems: "flex-start" }}>
                      <span style={{ fontSize: 13, lineHeight: 1 }}>{vs.icon}</span>
                      <span style={{ fontSize: 11, color: vs.color, fontWeight: 500 }}>{q.visa.label}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Additional steps (only when there are extra steps beyond the smart reply) ── */}
      {actionLines.length > 0 && (
        <div style={PAD}>
          <div style={SL}>Additional steps</div>
          <div style={{ background: "#f9fafb", border: "1px solid #efefef", borderRadius: 10, padding: "10px 12px" }}>
            {actionLines.slice(0, 3).map((step, i) => (
              <div key={i} style={{ display: "flex", gap: 7, padding: "4px 0", lineHeight: 1.4, marginTop: i > 0 ? 3 : 0, borderTop: i > 0 ? "1px solid #f3f4f6" : "none" }}>
                <div style={{ width: 17, height: 17, borderRadius: "50%", background: "#e5e7eb", color: "#6b7280", fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>{i + 1}</div>
                <span style={{ fontSize: 11, color: "#555555", lineHeight: 1.45 }}>{step}</span>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}
